import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faArrowDownToBracket,
  faArrowUpFromBracket,
  faCalendarDays,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCopy,
  faCube,
  faEllipsisV,
  faFile,
  faFileLines,
  faFileXls,
  faFloppyDisk,
  faPen,
  faPlay,
  faPlus,
  faSpinnerThird,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {StateConfigService} from '../../../../../../services/state/state-config.service';
import {
  buildRequirementsSummary,
  createDefaultSpecification,
  getIdsValidationIssues,
  getValueFieldDisplay,
  IDSFacet,
  IDSInfo,
  IDSRule,
  IDSSpecification,
  IDSValidationIssue,
  IDSValueField,
  isFacetInvalid,
  isIdsRuleValid,
  isValidIdsAuthor,
  isValidIsoDate
} from '../models/ids.model';
import {IdsSpecificationComponent} from '../ids-specification/ids-specification.component';
import {IdsAdapterService} from '../services/ids-adapter.service';
import {
  buildSharedParameterFile,
  ExistingSharedParameterFile,
  parseExclusionList,
  parseSharedParameterFile,
  REVIT_FALLBACK_DATA_TYPES,
  RevitDataType,
  RevitExportOptions,
} from '../models/revit-shared-parameters';
import {ChatService} from '../../../../../chatBIC/service/chat.service';
import {
  ATTRIBUTE_LIST_IFC_VERSIONS,
  AttributeListIfcVersion,
  IdsExcelImportService,
  IdsImportResult
} from '../services/ids-excel-import.service';
import {IdsExcelExportService} from '../services/ids-excel-export.service';
import {MyCheckUsage, RulesetService} from '../../../../../../services/ruleset.service';
import {
  IDS_THRESHOLDS_SUGGESTION,
  IdsThresholds,
  normalizeIdsThresholds,
  parseIdsThresholds,
  writeIdsThresholds,
} from '../../../../../../models/report/ids-thresholds';
import {RuleUsageWarningComponent} from '../../../../../ruleManagement/rule-usage-warning/rule-usage-warning.component';
import {IfcControllerService} from '../../../../../../api';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicDatepickerInputDirective,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicInputComponent,
  BicPopupComponent,
  BicSelectComponent,
  BicToastService,
  TooltipDirective
} from '@buildinginformationcloud/bic-ui-components';
import {PageHeaderComponent} from '../../../../../util/design/page-header/page-header.component';
import {EmptyStateComponent} from '../../../../../util/design/empty-state/empty-state.component';
import {PermissionService} from '../../../../../../services/permission/permission.service';
import {faChatbicAi} from '../../../../../util/icons/custom-icons';
import {MyCheckProposal, MyCheckProposalService} from '../../../../../../services/mycheck-proposal.service';
import {DiscardOutcome} from '../../../../../../guards/unsaved-changes.guard';
import {ChangeMarkerComponent} from '../../../../../util/design/change-marker/change-marker.component';
import {ProposalBannerComponent} from '../../../../../util/design/proposal-banner/proposal-banner.component';
import {RemovedRuleCardComponent} from '../../../../../util/design/removed-rule-card/removed-rule-card.component';
import {ProposalActionsComponent} from '../../../../../util/design/proposal-actions/proposal-actions.component';
import {MyCheckFieldChange, ProposalChangeStatus} from '../../../../../ruleManagement/proposal/mycheck-diff';
import {
  ProposalNavMarkerComponent,
  proposalNavTooltip
} from '../../../../../util/design/proposal-nav-marker/proposal-nav-marker.component';
import {ProposalStatusBadgeComponent} from '../../../../../util/design/proposal-status-badge/proposal-status-badge.component';
import {
  ProposalReviewContext,
  ProposalReviewHost,
  ProposalReviewItem,
  ProposalReviewService,
  ProposalReviewSnapshot,
  revealProposalTarget
} from '../../../../../../services/chat/proposal-review.service';
import {
  computeIdsDiff,
  facetPath,
  IdsDiff,
  IdsFacetBlock,
  IdsProposalDocument,
  RemovedIdsFacet
} from '../../../../../ruleManagement/proposal/ids-mycheck-diff';
import {RuleTestService} from '../../../../../../services/rule-test/rule-test.service';
import {
  RuleTestPopupContext,
  RuleTestQuota,
  RuleTestRuleOption,
  ruleTestContextKey,
} from '../../../../../../services/rule-test/rule-test.models';
import {ruleTestLimitTooltip} from '../../../../../../services/rule-test/rule-test.format';
import {RuleTestPopupComponent} from '../../../../../ruleManagement/shared/rule-test-popup/rule-test-popup.component';
import {buildDefaultReportConfigForRuleset} from '../../../../../my-checks/my-checks-builder/my-checks-builder.model';

export type EditorView = 'list' | 'metadata' | 'spec-detail' | 'removed-spec';
export type SpecDetailTab = 'general' | 'applicability' | 'requirements';

@Component({
  selector: 'app-ids-editor',
  templateUrl: './ids-editor.component.html',
  imports: [FormsModule, FaIconComponent, IdsSpecificationComponent, BicInputComponent, BicDatepickerInputDirective, BicButtonComponent, BicBadgeComponent, BicPopupComponent, BicSelectComponent, TooltipDirective, BicDropdownComponent, BicDropdownElementComponent, BicDropdownTriggerDirective, PageHeaderComponent, EmptyStateComponent, RuleUsageWarningComponent, ChangeMarkerComponent, ProposalBannerComponent, RemovedRuleCardComponent, RuleTestPopupComponent, ProposalActionsComponent, ProposalNavMarkerComponent, ProposalStatusBadgeComponent],
  standalone: true,
  host: {'class': 'flex flex-col h-full w-full'},
  // Blendet die nativen Auf/Ab-Pfeile der beiden Schwellwert-Felder aus (rein optisch, passt
  // sonst nicht zum übrigen Formular) - bic-input hat dafür kein eigenes Input, daher per
  // ::ng-deep auf das native <input>.
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    ::ng-deep input[type="number"]::-webkit-inner-spin-button,
    ::ng-deep input[type="number"]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    ::ng-deep input[type="number"] {
      -moz-appearance: textfield;
    }
  `]
})
export class IdsEditorComponent implements OnInit, AfterViewInit, OnDestroy, ProposalReviewHost {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private stateConfigService = inject(StateConfigService);
  private idsAdapter = inject(IdsAdapterService);
  private ifcControllerService = inject(IfcControllerService);
  private idsExcelImport = inject(IdsExcelImportService);
  private idsExcelExport = inject(IdsExcelExportService);
  private zone = inject(NgZone);
  private http = inject(HttpClient);
  private rulesetService = inject(RulesetService);
  private permissionService = inject(PermissionService);
  validationErrors: string[] = [];

  protected readonly faArrowUpFromBracket = faArrowUpFromBracket;
  protected readonly faArrowDownToBracket = faArrowDownToBracket;
  protected readonly faPlus = faPlus;
  protected readonly faFileLines = faFileLines;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faChevronLeft = faChevronLeft;
  /**
   * True, solange der chatBIC-Quickstart an diesem IDS-Regelwerk arbeitet.
   *
   * <p>Der Chat läuft auf der myChecks-Übersicht, nicht hier – dieses Flag ist ein **Schutz**: der
   * Agent schreibt das Regelwerk serverseitig, während dieses Formular noch den Stand vom Öffnen
   * hält. Ein „Übernehmen" würde seine Arbeit mit veralteten Spezifikationen überschreiben.
   */
  quickstartActive = false;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faCube = faCube;
  protected readonly faEllipsisV = faEllipsisV;
  protected readonly faPen = faPen;
  protected readonly faCopy = faCopy;
  protected readonly faTrash = faTrash;
  protected readonly faFileXls = faFileXls;
  protected readonly faFile = faFile;
  protected readonly faCalendarDays = faCalendarDays;
  protected readonly faFloppyDisk = faFloppyDisk;
  protected readonly faChatbicAi = faChatbicAi;
  private initialState = '';

  // ── Farbcodierung des Berichts (#2309) ────────────────────────────────────────────────────
  /**
   * Ist die eigene Farbcodierung eingeschaltet? Ohne Haken bleibt es beim bisherigen Verhalten:
   * der Bericht färbt binär über den IDS-Status. Bewusst keine stille Vorbelegung — die würde
   * jeden Bestandsbericht umfärben.
   */
  thresholdsEnabled = false;
  /** Eingabewerte als String, weil `bic-input` auch bei `type="number"` Strings liefert. */
  thresholdWarningFrom = '';
  thresholdSuccessFrom = '';
  /** Meldung bei unbrauchbarer Eingabe; `null` = gültig. */
  thresholdError: string | null = null;
  /**
   * Alles, was außer den Schwellwerten in `configJson` steht, bleibt beim Speichern erhalten —
   * der Editor kennt die fremden Schlüssel nicht und darf sie nicht wegwerfen.
   */
  private loadedConfigJson: string | null = null;

  /** Von chatBIC im Edit-Modus vorgeschlagene Änderung, bereits in den Formularzustand geschrieben
   *  (siehe {@link applyProposal}), aber noch nicht gespeichert. `null` = kein offener Vorschlag. */
  pendingProposal: MyCheckProposal | null = null;
  /** Unterschied zwischen dem Stand vor dem Vorschlag und dem Vorschlag – schrumpft mit jedem
   *  einzeln übernommenen oder verworfenen Feld bzw. jeder Spezifikation. */
  proposalDiff: IdsDiff | null = null;
  /** Ladezustand für die Banner-Buttons (Übernehmen/Verwerfen), getrennt von normalem Speichern. */
  applyingProposalAction = false;
  private proposalService = inject(MyCheckProposalService);
  private proposalSubscription?: Subscription;
  /** Kopplung an die Review-Leiste im Edit-Chat (#2789): solange sie dort steht, entfällt das Banner. */
  protected proposalReview = inject(ProposalReviewService);
  private unregisterReview?: () => void;
  protected readonly proposalNavTooltip = proposalNavTooltip;
  /** Formularzustand unmittelbar vor `applyProposal`, zum Wiederherstellen beim Verwerfen. */
  private preProposalSnapshot: IDSRule | null = null;
  /** Serialisierter Stand direkt nach `applyProposal`: unverändert und nichts übernommen → „Verwerfen" darf
   *  den Snapshot exakt zurückschreiben, sonst nur die offenen Änderungen (siehe {@link revertPendingProposal}). */
  private proposalAppliedState: string | null = null;
  /** true, sobald der Nutzer etwas einzeln **übernommen** hat (Verwerfen allein zählt nicht): beim
   *  Verlassen wird dann gespeichert, was er bestätigt hat, statt es zu verlieren. */
  private proposalHasAcceptedChanges = false;
  /** Siehe {@link diffKey}: Diff-Schlüssel je Spezifikationsobjekt. */
  private diffKeys = new WeakMap<IDSSpecification, string>();

  /** True, solange ungespeicherte Änderungen vorliegen (steuert Speichern/Zurück-Button, siehe save()). */
  get isDirty(): boolean {
    return !this.readOnly && !!this.initialState && this.initialState !== this.serializeEditorState();
  }

  private markSaved(): void {
    this.initialState = this.serializeEditorState();
  }

  /**
   * `expanded` ist reiner Akkordeon-UI-Zustand der Facetten und darf die Dirty-Prüfung nicht
   * triggern.
   *
   * Die Farbschwellen gehören dazu: sie stehen nicht in `rule` (sie sind kein Teil der IDS),
   * würden ohne diesen Zusatz aber nicht als Änderung gelten und beim Verlassen still verloren
   * gehen.
   */
  private serializeEditorState(): string {
    const state = {
      rule: this.rule,
      thresholds: this.thresholdsEnabled
        ? {warningFrom: this.thresholdWarningFrom, successFrom: this.thresholdSuccessFrom}
        : null,
    };
    return JSON.stringify(state, (key, value) => key === 'expanded' ? undefined : value);
  }

  /** If true, shows a back-arrow in the header (used in rule-management context) */
  @Input() showBackButton = false;

  /** Override route for back navigation. If set, navigateBack() goes here instead of bim-check. */
  @Input() backRoute: any[] | null = null;

  /** View-only Modus: bereitgestelltes globales Regelwerk ohne Bearbeitungsrecht. */
  @Input() readOnly = false;

  /** Die Regel gehört einem anderen Projekt und ist nur hierher geteilt – bearbeitet wird sie dort. */
  protected linkedFromOtherProject = false;

  /** Admin-Modus: globales Regelwerk ohne Projektkontext (Speichern an globale Endpunkte). */
  @Input() adminGlobal = false;

  /**
   * Testmodus „Prüfregel testen" (#2356) anbieten. Nur die Prüfregelverwaltung im Projekt setzt das
   * (`IdsRuleManagementEditorComponent`) – derselbe Editor läuft auch im bimCheck, dort gibt es keinen
   * Testlauf.
   */
  @Input() ruleTestEnabled = false;

  // ── Testmodus (#2356) ─────────────────────────────────────────────────────────────────────
  protected readonly faPlay = faPlay;
  private ruleTestService = inject(RuleTestService);
  /** Kontext des offenen Popups – beim Öffnen EINMAL berechnet, nie als Getter gebunden. */
  ruleTestContext: RuleTestPopupContext | null = null;
  ruleTestQuota: RuleTestQuota | null = null;
  /** Grüner Punkt am Button: ein Ergebnis liegt bereit, das noch niemand gesehen hat. */
  ruleTestUnseen = false;
  private ruleTestKey = '';
  /** Run-ID aus `?ruleTest=` – wird nach dem Laden des Regelwerks geöffnet. */
  private pendingRuleTestRunId: string | null = null;
  private ruleTestQuotaSubscription?: Subscription;
  private ruleTestUnseenSubscription?: Subscription;
  private unregisterRuleTestEditor?: () => void;
  /** Zuletzt geöffnete Spezifikation – Vorbelegung, auch wenn gerade die Liste offen ist. */
  private lastSelectedSpecName: string | null = null;
  protected readonly faSpinnerThird = faSpinnerThird;


  /** Zeitlimit für Import-Konvertierungen, damit ein hängendes Backend nicht in endlosem Laden endet. */
  private static readonly IMPORT_TIMEOUT_MS = 30000;

  /** Import-Popup State */
  showImportPopup = false;
  selectedImportFileName: string | null = null;
  selectedImportFile: File | null = null;
  importType: 'ids-file' | 'ids-excel' | 'attribute-list' | null = null;
  importLoading = false;
  importCompleted = false;
  importResponseMessage: string | null = null;

  /** Ob die aktuelle Import-Meldung einen Fehler beschreibt (statt einer Erfolgsmeldung). */
  get importFailed(): boolean {
    return !!this.importResponseMessage && !this.importCompleted;
  }

  /** Aufklappbare Detailanzeige: rohe Fehlermeldung ODER Warnungen eines Excel-Imports (z.B. übersprungene Sheets). */
  importErrorDetails: string | null = null;
  showImportErrorDetails = false;
  /** Attributlisten-Import: Zusatzfelder */
  ifcVersion: string | null = null;
  protected readonly attributeListIfcVersions = ATTRIBUTE_LIST_IFC_VERSIONS;
  levelOfInformation: number | null = null;
  discipline: string = '';
  importReplaceSpecifications = true;
  importOverwriteMetadata = true;

  /** Export-Popup State (IDS + Revit-Shared-Parameter + IDS-Excel, per Tab) */
  showExportPopup = false;
  exportTab: 'ids' | 'revit' | 'ids-vorlage' | 'attributliste' = 'ids';
  selectedRevitFileName: string | null = null;
  private selectedRevitFile: File | null = null;
  revitExportGroupBy: RevitExportOptions['groupBy'] = 'specification';
  revitExportNameScheme: RevitExportOptions['nameScheme'] = 'property';
  revitExportFallbackDataType: RevitDataType = 'TEXT';
  revitExportKeepOrphans = true;
  /** Ausschlusslisten des Shared-Parameter-Exports (#2498), als Roheingabe - siehe `parseExclusionList`. */
  revitExportExcludedProperties = '';
  revitExportExcludedPropertySets = '';
  protected readonly revitFallbackDataTypes = REVIT_FALLBACK_DATA_TYPES;
  revitExportResultMessage: string | null = null;
  revitExportWarnings: string[] | null = null;
  /** Ladezustand/Fehlermeldung der beiden Excel-Exporte (IDS-Vorlage, Attributliste). */
  excelExportLoading = false;
  excelExportErrorMessage: string | null = null;

  /** Set to true after user clicks "Übernehmen" – activates error highlighting on empty required fields */
  submitted = false;
  private toastService = inject(BicToastService);
  /** Für den Quickstart-Schutz: Zustand und Abschluss-Signal des chatBIC-Quickstarts. */
  private chatService = inject(ChatService);
  private quickstartCompletedSubscription?: Subscription;
  private validationIssues: IDSValidationIssue[] = [];

  /** ID der LOICheckRule im Backend (null = neue Rule) */
  ruleId: string | null = null;
  /** Index der Spezifikation, für die die Entfernen-Bestätigung offen ist (null = kein Popup) */
  specIndexToRemove: number | null = null;
  specNameToRemove = '';
  /** myChecks, die die zu entfernende Spezifikation verwenden */
  deleteUsages: MyCheckUsage[] = [];
  deleteUsagesLoading = false;

  /** Welche Ansicht rechts angezeigt wird */
  currentView: EditorView = 'list';

  /** Aktiver Tab in der Spezifikation-Detailansicht */
  activeDetailTab: SpecDetailTab = 'general';

  @ViewChildren('applicabilityContainer') applicabilityContainers!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('requirementsContainer') requirementsContainers!: QueryList<ElementRef<HTMLDivElement>>;

  private resizeObserver: ResizeObserver | null = null;
  private observedElements = new Set<Element>();

  rule: IDSRule = {
    info: {
      title: '',
      copyright: '',
      version: '',
      description: '',
      author: '',
      date: '',
      purpose: '',
      milestone: ''
    },
    specifications: []
  };

  selectedSpecIndex = -1;
  /** Die in der Seitenleiste angeklickte, vom Vorschlag entfernte Spezifikation (Karte rechts). */
  selectedRemovedSpec: IDSSpecification | null = null;

  get selectedSpec(): IDSSpecification | null {
    if (this.selectedSpecIndex >= 0 && this.selectedSpecIndex < this.rule.specifications.length) {
      return this.rule.specifications[this.selectedSpecIndex];
    }
    return null;
  }

  ngOnInit(): void {
    // Check if editing an existing rule (via route param or navigation state)
    const state = history.state;
    const storedRuleId = sessionStorage.getItem('ids-edit-ruleId');

    if (storedRuleId) {
      // Höchste Priorität: sessionStorage (gesetzt vom myCheck-Wrapper beim Bearbeiten)
      sessionStorage.removeItem('ids-edit-ruleId');
      this.ruleId = storedRuleId;
      this.loadRuleFromBackend(this.ruleId!);
    } else if (state?.idsRule) {
      // Passed directly via navigation state (e.g. from list)
      this.rule = state.idsRule;
      this.ruleId = state.ruleId ?? null;
      if (this.rule.specifications.length > 0) {
        this.selectedSpecIndex = 0;
      }
    } else if (state?.ruleId) {
      // Load from backend by ID (via navigation state)
      this.ruleId = state.ruleId;
      this.loadRuleFromBackend(this.ruleId!);
    }
    // Neue Regel: Name und Beschreibung aus dem Navigation-State übernehmen
    if (state?.ruleName) {
      this.rule.info.title = state.ruleName;
    }
    if (state?.ruleDescription) {
      this.rule.info.description = state.ruleDescription;
    }
    // Für die beiden Backend-Lade-Zweige überschreibt loadRuleFromBackend() diesen Stand, sobald die
    // Antwort da ist. Bis dahin ist der aktuelle (noch leere/State-basierte) Stand der Referenzpunkt.
    this.markSaved();

    this.initQuickstart();

    // Vorschlags-Signal aus dem Edit-Modus-Chat. Für IDS ist die rulesetId des Bots die ID dieser
    // Regel (workflow_check_rules), `loadAndApplyProposal` prüft readOnly/adminGlobal selbst.
    this.proposalSubscription = this.chatService.mycheckProposalReceived.subscribe(event => {
      if (this.ruleId && event.rulesetId === this.ruleId) this.loadAndApplyProposal(event.proposalId);
    });

    this.initRuleTest();

    this.unregisterReview = this.proposalReview.register(this);
  }

  /** Öffnet den Chat im Edit-Modus für dieses (bereits gespeicherte) IDS-myCheck. */
  openEditChat(): void {
    if (this.adminGlobal || this.readOnly || !this.ruleId) return;
    void this.chatService.openMycheckEditChat(this.ruleId, 'IDS');
  }

  /**
   * Verlässt den Quickstart und gibt das Formular frei.
   *
   * <p>Lädt das Regelwerk dabei **neu**: der Agent hat es serverseitig geschrieben, das Formular
   * hält noch den alten Stand. Ohne Neuladen zeigt der Editor die alten Spezifikationen und ein
   * „Übernehmen" würde die Arbeit des Agenten verwerfen.
   */
  leaveQuickstart(reload: boolean = true): void {
    this.quickstartActive = false;
    this.chatService.closeMycheckSurface();
    if (this.ruleId) {
      this.chatService.forgetMycheckQuickstart(this.ruleId);
      if (reload) {
        this.loadRuleFromBackend(this.ruleId);
      }
    }
  }

  /** @returns ob gespeichert wird – `false`, wenn die Validierung (oder ein laufender Quickstart) es verhindert. */
  save(): boolean {
    // Während der Agent schreibt, ist Speichern gesperrt: das Formular hält den Stand vom Öffnen
    // und würde die Spezifikationen des Agenten überschreiben. Das Template versteckt den Button,
    // aber save() ist auch aus dem Code erreichbar.
    if (this.readOnly || this.quickstartActive) return false;
    this.submitted = true;
    this.validationIssues = getIdsValidationIssues(this.rule);
    this.validationErrors = this.validationIssues.map(issue => issue.message);
    if (!this.isValid) {
      this.toastService.warning('Bitte ergänze die Pflichtfelder in den markierten IDS-Bereichen.');
      this.navigateToFirstError();
      return false;
    }

    // Speichern mit offenem Vorschlag übernimmt ihn: das Formular enthält ihn bereits. Als übernommen
    // gilt er erst, wenn der Request durch ist – scheitert die Validierung oder das Speichern, bleibt er
    // offen, und der nächste Speichern-Klick schließt ihn ab.
    const proposal = this.pendingProposal;

    const xml = this.idsAdapter.toXml(this.rule);
    const projectId = this.stateConfigService.getProjectId();
    const basePath = (this.ifcControllerService as any).basePath ?? '';

    // Save to generic workflow_check_rules table
    const checkRule: any = {
      id: this.ruleId ?? undefined,
      name: this.rule.info.title || 'IDS Regel',
      description: this.rule.info.description || null,
      category: 'IDS',
      projectId: this.adminGlobal ? null : projectId,
      ruleData: xml,
      ruleDataFormat: 'application/xml',
      active: true,
      // Immer ein JSON-String, nie `null`: das Backend liest `null` als "unveraendert", ein
      // Abwaehlen der eigenen Farbcodierung kaeme so nie an. Fremde Schluessel bleiben erhalten.
      configJson: writeIdsThresholds(this.loadedConfigJson, this.currentThresholds()),
    };

    // Admin-Modus: globaler Draft-Endpunkt (projektlos), sonst projekt-scoped.
    let request$;
    if (this.adminGlobal) {
      request$ = this.ruleId
        ? this.http.put<any>(`${basePath}/workflows/rules/global/${this.ruleId}`, checkRule)
        : this.http.post<any>(`${basePath}/workflows/rules/global`, checkRule);
    } else {
      request$ = this.ruleId
        ? this.http.put<any>(`${basePath}/workflows/rules/${this.ruleId}`, checkRule, {params: {projectId: String(projectId)}})
        : this.http.post<any>(`${basePath}/workflows/rules`, checkRule, {params: {projectId: String(projectId)}});
    }

    request$.subscribe({
      next: (savedRule: any) => {
        const wasNew = !this.ruleId;
        this.ruleId = savedRule.id ?? null;
        // Ein neues IDS ist erst mit seiner ID testbar – ab jetzt beim Testmodus anmelden.
        if (wasNew && this.ruleId) this.registerRuleTestEditor();
        // Der Server kann die Konfiguration normalisiert haben; ohne das Nachziehen zeigte der
        // Editor beim naechsten Speichern eine veraltete Basis fuer fremde Schluessel.
        this.loadedConfigJson = savedRule.configJson ?? this.loadedConfigJson;
        this.markSaved();
        this.markPendingProposalApplied(proposal);
        this.toastService.success('IDS-Regel gespeichert');
      },
      error: (err) => {
        console.error('Error saving IDS rule:', err);
        this.toastService.error('Speichern der IDS-Regel fehlgeschlagen');
      }
    });
    return true;
  }

  ngOnDestroy(): void {
    this.ruleTestQuotaSubscription?.unsubscribe();
    this.ruleTestUnseenSubscription?.unsubscribe();
    this.unregisterRuleTestEditor?.();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.observedElements.clear();
    this.quickstartCompletedSubscription?.unsubscribe();
    this.proposalSubscription?.unsubscribe();
    this.unregisterReview?.();
    // Nur den Edit-Chat zurücksetzen: sonst bliebe der schwebende Chat app-weit an diesem myCheck
    // hängen. Ein laufender Quickstart (Fläche 'mycheck') bleibt unberührt.
    if (this.chatService.activeSurface === 'edit') {
      this.chatService.closeMycheckSurface();
    }
  }

  /**
   * Erkennt einen laufenden Quickstart für dieses Regelwerk und schützt das Formular davor.
   *
   * <p>Gegenstück zu {@code KiEditorComponent.initQuickstart}: seit der Agent auch IDS-myChecks
   * baut, kann der Nutzer auch hier mitten in dessen Arbeit landen (Expertenmodus-Sprung).
   */
  private initQuickstart(): void {
    if (this.adminGlobal || this.readOnly || !this.ruleId) return;
    if (!this.chatService.isMycheckQuickstartActive(this.ruleId)) return;

    void this.verifyAndLockQuickstart(this.ruleId);
  }

  /**
   * Prüft, ob der gemerkte Quickstart noch eine echte, laufende Session ist, bevor das Formular
   * gesperrt wird.
   *
   * <p>Der Aktiv-Merker allein reicht nicht: er überlebt auch, wenn der Nutzer den Quickstart durch
   * Wegnavigieren statt über „Übernehmen"/„Manuell bearbeiten" verlassen hat. Ohne diese Prüfung
   * blieb das Formular dann bei jedem erneuten Öffnen dauerhaft gesperrt, obwohl gar kein Agent
   * mehr daran arbeitet.
   */
  private async verifyAndLockQuickstart(ruleId: string): Promise<void> {
    let resumable: boolean;
    try {
      resumable = await this.chatService.canResumeMycheckQuickstart(ruleId);
    } catch {
      // Unklar (z.B. Drosselung durch ACS) ist nicht dasselbe wie "weg": der Merker bleibt, aber
      // das Formular sperrt sich in diesem Aufruf nicht fälschlich.
      return;
    }
    // Der Nutzer kann inzwischen ein anderes Regelwerk geöffnet haben.
    if (this.ruleId !== ruleId) return;
    if (!resumable) {
      this.chatService.forgetMycheckQuickstart(ruleId);
      return;
    }

    this.quickstartActive = true;
    this.quickstartCompletedSubscription = this.chatService.mycheckRulesetCompleted.subscribe(() => {
      this.onQuickstartCompleted();
    });
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();

    // Re-observe when the list of containers changes (e.g. specs added/removed, view switch)
    this.applicabilityContainers.changes.subscribe(() => this.observeAllContainers());
    this.requirementsContainers.changes.subscribe(() => this.observeAllContainers());
  }

  private onQuickstartCompleted(): void {
    this.leaveQuickstart(true);
    this.toastService.success('IDS-myCheck ist fertig – Formular aktualisiert');
  }

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.zone.runOutsideAngular(() => {
        this.recalculateAllOverflows();
      });
    });
    this.observeAllContainers();
  }

  private observeAllContainers(): void {
    if (!this.resizeObserver) return;

    // Unobserve elements that are no longer in the DOM
    const currentElements = new Set<Element>();
    const allContainers = [
      ...(this.applicabilityContainers?.toArray() ?? []),
      ...(this.requirementsContainers?.toArray() ?? [])
    ];

    for (const ref of allContainers) {
      currentElements.add(ref.nativeElement);
      if (!this.observedElements.has(ref.nativeElement)) {
        this.resizeObserver.observe(ref.nativeElement);
        this.observedElements.add(ref.nativeElement);
      }
    }

    // Unobserve removed elements
    for (const el of this.observedElements) {
      if (!currentElements.has(el)) {
        this.resizeObserver.unobserve(el);
        this.observedElements.delete(el);
      }
    }

    // Initial calculation
    this.recalculateAllOverflows();
  }

  private recalculateAllOverflows(): void {
    this.calculateOverflow(this.applicabilityContainers);
    this.calculateOverflow(this.requirementsContainers);
  }

  private calculateOverflow(containers: QueryList<ElementRef<HTMLDivElement>>): void {
    if (!containers) return;
    containers.forEach((containerRef) => {
      const container = containerRef.nativeElement;
      const children = Array.from(container.children) as HTMLElement[];
      const badges = children.filter(c => c.tagName === 'BIC-BADGE');
      const overflowCounter = children.find(c => c.classList.contains('overflow-counter'));

      if (badges.length === 0) {
        if (overflowCounter) overflowCounter.style.display = 'none';
        return;
      }

      // Step 1: Show all badges, hide counter to measure natural layout
      badges.forEach(b => b.style.removeProperty('display'));
      if (overflowCounter) overflowCounter.style.display = 'none';

      // Step 2: Determine the first row boundary
      const firstBadgeRect = badges[0].getBoundingClientRect();
      const firstRowBottom = firstBadgeRect.bottom + 4; // 4px tolerance for gap

      // Step 3: Find first badge that wraps to next row
      let firstOverflowIdx = badges.length;
      for (let i = 1; i < badges.length; i++) {
        if (badges[i].getBoundingClientRect().top >= firstRowBottom) {
          firstOverflowIdx = i;
          break;
        }
      }

      let hiddenCount = badges.length - firstOverflowIdx;

      // Step 4: If there are overflowing badges, hide them and show counter
      if (hiddenCount > 0) {
        for (let i = firstOverflowIdx; i < badges.length; i++) {
          badges[i].style.display = 'none';
        }

        if (overflowCounter) {
          overflowCounter.textContent = hiddenCount + '+';
          overflowCounter.style.display = '';

          // Check if the counter itself causes wrapping of the last visible badge
          if (firstOverflowIdx > 0) {
            const counterTop = overflowCounter.getBoundingClientRect().top;
            if (counterTop >= firstRowBottom) {
              // Counter pushed to next line, hide one more badge
              firstOverflowIdx--;
              hiddenCount = badges.length - firstOverflowIdx;
              badges[firstOverflowIdx].style.display = 'none';
              overflowCounter.textContent = hiddenCount + '+';
            }
          }
        }
      } else {
        if (overflowCounter) overflowCounter.style.display = 'none';
      }
    });
  }

  /** Generates a description fragment for a single facet */
  getFacetDescriptionFragment(facet: IDSFacet, context: 'applicability' | 'requirements'): string {
    switch (facet.type) {
      case 'entity': return (context === 'applicability' ? 'der Entität ' : 'die Entität ') + getValueFieldDisplay(facet.entityName);
      case 'entityGroup': return (context === 'applicability' ? 'der Entitätengruppe ' : 'die Entitätengruppe ') + getValueFieldDisplay(facet.entityName);
      case 'attribute': return (context === 'applicability' ? 'Attribut ' : 'das Attribut ') + getValueFieldDisplay(facet.attributeName);
      case 'property': return 'die Eigenschaft ' + getValueFieldDisplay(facet.propertyName);
      case 'classification': return 'die Klassifikation ' + getValueFieldDisplay(facet.classificationSystem);
      case 'material': return 'das IfcMaterial ' + getValueFieldDisplay(facet.materialName);
      case 'partOf': return 'Teil von ' + getValueFieldDisplay(facet.partOfEntityName);
      default: return '...';
    }
  }

  /** Natürlichsprachlicher Hinweistext für die Anforderungen, gruppiert nach Anwendbarkeit (Erforderlich/Optional/Verboten). */
  requirementsSummary(facets: IDSFacet[]): string {
    return buildRequirementsSummary(facets);
  }

  /** Returns a short badge text for a facet (e.g. "Entität: IfcWall") */
  getBadgeText(facet: IDSFacet): string {
    const labels: Record<string, string> = {
      entity: 'Entität', entityGroup: 'Entitätengruppe', attribute: 'Attribut', property: 'Eigenschaft',
      classification: 'Klassifikation', material: 'Material', partOf: 'Teil von'
    };
    const fields: Record<string, () => IDSValueField> = {
      entity: () => facet.entityName, entityGroup: () => facet.entityName, attribute: () => facet.attributeName,
      property: () => facet.propertyName, classification: () => facet.classificationSystem,
      material: () => facet.materialName, partOf: () => facet.partOfEntityName
    };
    const label = labels[facet.type] ?? facet.type;
    const display = getValueFieldDisplay(fields[facet.type]?.(), '');
    return display ? `${label}: ${display}` : label;
  }

  /**
   * Konvertiert einen vom Datepicker gelieferten Wert (Date oder String) in das
   * von IDS geforderte xs:date-Format (YYYY-MM-DD). Ist der Wert leer oder nicht
   * interpretierbar, wird ein leerer String bzw. der getrimmte Originalwert zurückgegeben.
   */
  formatIdsDate(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    let date: Date;
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return '';
      }
      // Bereits im IDS-Format (YYYY-MM-DD)
      const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
      if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
      }
      // dd.MM.yyyy oder dd/MM/yyyy
      const dmyMatch = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/.exec(trimmed);
      if (dmyMatch) {
        date = new Date(Number(dmyMatch[3]), Number(dmyMatch[2]) - 1, Number(dmyMatch[1]));
      } else {
        date = new Date(trimmed);
      }
    } else {
      return '';
    }

    if (isNaN(date.getTime())) {
      return typeof value === 'string' ? value.trim() : '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /** Metadaten-Banner klick: Toggle zwischen list und metadata */
  toggleMetadata() {
    if (this.currentView === 'metadata') {
      this.currentView = 'list';
    } else {
      this.currentView = 'metadata';
    }
  }

  /** Spezifikation auswählen und Detailansicht öffnen / schließen */
  selectSpecification(index: number) {
    if (this.selectedSpecIndex === index && this.currentView === 'spec-detail') {
      // Erneuter Klick auf dieselbe Spezifikation → zurück zur Listenansicht
      this.currentView = 'list';
      this.selectedSpecIndex = -1;
    } else {
      this.selectedSpecIndex = index;
      this.currentView = 'spec-detail';
      this.activeDetailTab = 'general';
      this.lastSelectedSpecName = this.rule.specifications[index]?.name ?? null;
    }
  }

  /** Neue Spezifikation erstellen und Detailansicht öffnen */
  addSpecification() {
    const spec = createDefaultSpecification();
    this.rule.specifications.push(spec);
    this.selectedSpecIndex = this.rule.specifications.length - 1;
    this.currentView = 'spec-detail';
    this.activeDetailTab = 'general';
  }

  /** Zurück zur Listenansicht */
  backToList() {
    this.currentView = 'list';
  }

  /**
   * Entfernen einer Spezifikation anfragen. Spezifikationen sind keine eigenen Entitäten – sie
   * werden erst beim Speichern der neu serialisierten IDS-XML wirklich entfernt. Ist die
   * Spezifikation in myChecks referenziert (über die synthetische ID `<ruleId>::<name>`), wird das
   * vorher angezeigt: beim Speichern verschwindet die Zuordnung dort.
   */
  removeSpecification(index: number) {
    const specification = this.rule.specifications[index];
    const specName = specification?.name ?? '';
    if (!this.ruleId || !specName) {
      this.applySpecificationRemoval(index);
      return;
    }

    this.specIndexToRemove = index;
    this.specNameToRemove = specName;
    this.deleteUsages = [];
    this.deleteUsagesLoading = true;
    this.rulesetService.findMyCheckUsages([`${this.ruleId}::${specName}`]).subscribe({
      next: usages => {
        if (this.specIndexToRemove !== index) return;
        // Nicht verwendet → keine Rückfrage nötig, direkt entfernen (bisheriges Verhalten).
        if (usages.length === 0) {
          this.cancelRemoveSpecification();
          this.applySpecificationRemoval(index);
          return;
        }
        this.deleteUsages = usages;
        this.deleteUsagesLoading = false;
      },
      error: () => {
        if (this.specIndexToRemove !== index) return;
        this.cancelRemoveSpecification();
        this.applySpecificationRemoval(index);
      },
    });
  }

  cancelRemoveSpecification(): void {
    this.specIndexToRemove = null;
    this.specNameToRemove = '';
    this.deleteUsages = [];
    this.deleteUsagesLoading = false;
  }

  confirmRemoveSpecification(): void {
    const index = this.specIndexToRemove;
    if (index === null) return;
    this.cancelRemoveSpecification();
    this.applySpecificationRemoval(index);
  }

  private applySpecificationRemoval(index: number) {
    const [removedSpec] = this.rule.specifications.splice(index, 1);
    if (this.selectedSpecIndex >= this.rule.specifications.length) {
      this.selectedSpecIndex = this.rule.specifications.length - 1;
    }
    if (this.selectedSpecIndex < 0) {
      this.currentView = 'list';
    }
    if (removedSpec) this.dropRemovedSpecFromProposal(removedSpec);
  }

  /**
   * Löscht der Nutzer bei offenem Vorschlag eine Spezifikation über das Menü, kann niemand mehr über ihre
   * offenen Änderungen entscheiden: Sie fallen aus dem Diff (eine gleichnamige Spezifikation behält ihre),
   * sonst zählte die Review-Leiste sie weiter, ohne dass es dafür ✓/✗ gibt.
   */
  private dropRemovedSpecFromProposal(spec: IDSSpecification): void {
    const specChanges = this.proposalDiff?.specChanges;
    if (!specChanges) return;
    specChanges.added = specChanges.added.filter(added => added !== spec);
    const key = this.diffKey(spec);
    if (!this.rule.specifications.some(other => this.diffKey(other) === key)) {
      specChanges.changed.delete(key);
      specChanges.removedFacets.delete(key);
    }
    this.settleProposalIfResolved();
  }

  /** Spezifikation bearbeiten (Detailansicht öffnen) */
  editSpecification(index: number) {
    this.selectedSpecIndex = index;
    this.currentView = 'spec-detail';
    this.activeDetailTab = 'general';
  }

  /** Spezifikation duplizieren */
  duplicateSpecification(index: number) {
    const original = this.rule.specifications[index];
    const copy: IDSSpecification = JSON.parse(JSON.stringify(original));
    copy.id = crypto.randomUUID();
    copy.name = (copy.name || 'Unbenannte Spezifikation') + ' (Kopie)';
    // Neue IDs für alle Facets generieren
    copy.applicability.forEach(f => f.id = crypto.randomUUID());
    copy.requirements.forEach(f => f.id = crypto.randomUUID());
    this.rule.specifications.splice(index + 1, 0, copy);
  }

  onSpecificationChange(spec: IDSSpecification) {
    if (this.selectedSpecIndex >= 0) {
      // `IdsSpecificationComponent` meldet eine Kopie. Eine vom Vorschlag neu angelegte Spezifikation
      // ist über ihre Objekt-Referenz gemerkt – ohne das Nachziehen verlöre sie nach der ersten
      // Eingabe ihre Markierung und ließe sich nicht mehr verwerfen.
      const added = this.proposalDiff?.specChanges.added;
      const addedIndex = added?.indexOf(this.rule.specifications[this.selectedSpecIndex]) ?? -1;
      if (added && addedIndex >= 0) added[addedIndex] = spec;
      const diffKey = this.diffKeys.get(this.rule.specifications[this.selectedSpecIndex]);
      if (diffKey !== undefined) this.diffKeys.set(spec, diffKey);
      this.rule.specifications[this.selectedSpecIndex] = spec;
    }
  }

  openValidationIssue(issueIndex: number): void {
    const issue = this.validationIssues[issueIndex];
    if (!issue) return;

    if (issue.section === 'metadata') {
      if (issue.specificationIndex != null) {
        this.selectedSpecIndex = issue.specificationIndex;
      }
      this.currentView = 'metadata';
      return;
    }

    if (issue.specificationIndex == null || !issue.section) return;

    this.selectedSpecIndex = issue.specificationIndex;
    this.currentView = 'spec-detail';
    this.activeDetailTab = issue.section;
    const facet = this.rule.specifications[issue.specificationIndex]?.[issue.section].find(item => item.id === issue.facetId);
    if (facet) facet.expanded = true;
  }

  /** Reactive validation check – used by template for button state / tooltip */
  get isValid(): boolean {
    return isIdsRuleValid(this.rule);
  }

  /** True if a date is entered but does not match the required ISO format (YYYY-MM-DD). */
  get isDateInvalid(): boolean {
    return !!this.rule.info.date?.trim() && !isValidIsoDate(this.rule.info.date);
  }

  /** True if an author is entered but is not an e-mail address (required by the IDS schema). */
  get isAuthorInvalid(): boolean {
    return !!this.rule.info.author?.trim() && !isValidIdsAuthor(this.rule.info.author);
  }

  private loadRuleFromBackend(ruleId: string) {
    const basePath = (this.ifcControllerService as any).basePath ?? '';
    const projectId = this.stateConfigService.getProjectId();
    this.http.get<any>(`${basePath}/workflows/rules/${ruleId}`, {
      params: {projectId: String(projectId)}
    }).subscribe({
      next: (checkRule: any) => {
        this.ruleId = checkRule.id ?? ruleId;
        // Selbstschutz gegen Deep-Links: `readOnly` kommt sonst nur aus dem Query-Parameter,
        // ein Link ohne `?readOnly=true` öffnete ein zentral bereitgestelltes Regelwerk voll
        // editierbar. Bereitgestellte Regeln (kein Projektbezug) sind nur zum Ansehen,
        // Ausführen und Duplizieren gedacht; bearbeitet werden sie im Admin-Bereich.
        if (!this.adminGlobal && checkRule.projectId == null
          && !this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN')) {
          this.readOnly = true;
        }
        // Dasselbe für eine aus einem anderen Projekt geteilte Regel: bearbeitet wird sie nur im
        // Eigentümerprojekt – auch mit Regelwerk-Admin-Recht.
        if (!this.adminGlobal && checkRule.projectId != null && !!projectId
          && Number(checkRule.projectId) !== Number(projectId)) {
          this.linkedFromOtherProject = true;
          this.readOnly = true;
        }
        if (checkRule.category === 'IDS' && checkRule.ruleData) {
          this.rule = this.idsAdapter.fromXml(checkRule.ruleData);
          if (this.rule.specifications.length > 0) {
            this.selectedSpecIndex = 0;
          }
        }
        // Update title/description from stored rule if not yet set from XML
        if (!this.rule.info.title && checkRule.name) {
          this.rule.info.title = checkRule.name;
        }
        if (!this.rule.info.description && checkRule.description) {
          this.rule.info.description = checkRule.description;
        }
        this.applyLoadedConfig(checkRule.configJson);
        this.markSaved();
        this.openPendingRuleTest();
        // Ein zwischenzeitlich (z. B. vor einem Reload) erzeugter Vorschlag soll nicht verloren
        // gehen – die Erkennung ist sonst ausschließlich an das Live-Chat-Signal gebunden.
        this.loadPendingProposalIfAny();
      },
      error: (err) => console.error('Error loading IDS rule:', err)
    });
  }

  /** Schaltet die eigene Farbcodierung ein/aus; beim Einschalten mit dem Vorschlagswert. */
  toggleThresholds(): void {
    if (this.readOnly) return;
    this.thresholdsEnabled = !this.thresholdsEnabled;
    if (this.thresholdsEnabled && !this.thresholdWarningFrom && !this.thresholdSuccessFrom) {
      this.thresholdWarningFrom = String(IDS_THRESHOLDS_SUGGESTION.warningFrom);
      this.thresholdSuccessFrom = String(IDS_THRESHOLDS_SUGGESTION.successFrom);
    }
    this.onThresholdChange();
  }

  /** Validiert die Eingabe; die Meldung erscheint statt der Vorschau. */
  onThresholdChange(): void {
    this.thresholdError = this.thresholdsEnabled && !this.currentThresholds()
      ? 'Bitte zwei Werte zwischen 0 und 100 angeben.'
      : null;
  }

  /**
   * Die drei Bereiche als Vorschau, genau in der Sprache des Berichts.
   *
   * Bei gleichen Werten (z. B. 100/100) entfällt die Warnstufe — dann sind es nur zwei Bereiche,
   * und die Vorschau soll keine leere Spanne behaupten.
   */
  get thresholdPreview(): Array<{ label: string; classes: string }> {
    const thresholds = this.currentThresholds();
    if (!thresholds) return [];
    const {warningFrom, successFrom} = thresholds;
    const bands = [{label: `Nicht erfüllt: unter ${warningFrom} %`, classes: 'bg-red/10 text-red'}];
    if (successFrom > warningFrom) {
      bands.push({
        label: `Teilweise erfüllt: ${warningFrom} bis unter ${successFrom} %`,
        classes: 'bg-yellow/20 text-gray-900',
      });
    }
    bands.push({label: `Erfüllt: ab ${successFrom} %`, classes: 'bg-green/10 text-green'});
    return bands;
  }

  // ── Testmodus „Prüfregel testen" (#2356) ───────────────────────────────────────────────────

  /** Button sichtbar: nur in der Prüfregelverwaltung eines Projekts, nie readOnly/Admin/Quickstart. */
  get canShowRuleTest(): boolean {
    return this.ruleTestEnabled && !this.readOnly && !this.adminGlobal && !this.quickstartActive;
  }

  /** Startbar erst mit gespeicherter ID (Rechte und Besitz hängen am Set) und freiem Kontingent. */
  get ruleTestDisabled(): boolean {
    return !this.ruleId || this.ruleTestLimitReached;
  }

  get ruleTestLimitReached(): boolean {
    return this.ruleTestQuota?.remaining === 0;
  }

  /** `undefined` statt `''`: die Tooltip-Direktive zeigt sonst einen leeren Pfeil. */
  get ruleTestTooltip(): string {
    if (!this.ruleId) return 'Speichere das IDS zuerst, um es zu testen.';
    return ruleTestLimitTooltip(this.ruleTestQuota)?.title ?? undefined as unknown as string;
  }

  get ruleTestTooltipSubtext(): string {
    if (!this.ruleId) return undefined as unknown as string;
    return ruleTestLimitTooltip(this.ruleTestQuota)?.text ?? undefined as unknown as string;
  }

  private initRuleTest(): void {
    if (!this.ruleTestEnabled || this.adminGlobal) return;
    this.pendingRuleTestRunId = this.route.snapshot.queryParamMap.get('ruleTest');
    this.ruleTestQuotaSubscription = this.ruleTestService.quota$.subscribe(quota => this.ruleTestQuota = quota);
    this.ruleTestService.refreshQuota();
    if (this.ruleId) this.registerRuleTestEditor();
  }

  /** Meldet diesen Editor für Toast-Link und Punkt am Button an – sobald die ID des IDS feststeht. */
  private registerRuleTestEditor(): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!this.ruleTestEnabled || this.adminGlobal || !projectId || !this.ruleId) return;
    this.ruleTestUnseenSubscription?.unsubscribe();
    this.unregisterRuleTestEditor?.();
    this.ruleTestKey = ruleTestContextKey(projectId, this.ruleId);
    this.ruleTestUnseenSubscription = this.ruleTestService.hasUnseenResult$(this.ruleTestKey)
      .subscribe(unseen => this.ruleTestUnseen = unseen);
    this.unregisterRuleTestEditor = this.ruleTestService.registerEditor(
      this.ruleTestKey, [(this.router.url ?? '').split('?')[0]], () => this.openRuleTest(),
    );
  }

  /** Nach dem Laden: ein über `?ruleTest=` mitgegebener Lauf öffnet direkt das Popup. */
  private openPendingRuleTest(): void {
    const runId = this.pendingRuleTestRunId;
    this.pendingRuleTestRunId = null;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!runId || !projectId || !this.canShowRuleTest || !this.ruleId) return;
    if (!this.ruleTestKey) this.registerRuleTestEditor();
    this.ruleTestService.attach(projectId, this.ruleTestKey, runId);
    this.openRuleTest();
  }

  openRuleTest(): void {
    if (!this.canShowRuleTest || !this.ruleId) return;
    this.ruleTestContext = this.buildRuleTestContext();
  }

  closeRuleTest(): void {
    this.ruleTestContext = null;
  }

  /**
   * Friert den ungespeicherten Editor-Stand ein und beschreibt ihn für das Popup. Eine „Prüfregel"
   * ist bei IDS eine Spezifikation; ihr Name ist der Schlüssel (`ruleKey`), denn die `spec.id` ist
   * eine bei jedem Laden neu gewürfelte UUID. Getestet wird immer genau eine Spezifikation – der
   * Request trägt die IDS-XML des Stands, auf diese eine reduziert.
   */
  buildRuleTestContext(): RuleTestPopupContext {
    const projectId = this.stateConfigService.getProjectIdOrUndefined() ?? 0;
    const frozen: IDSRule = structuredClone(this.rule);
    const names = frozen.specifications.map(spec => (spec.name ?? '').trim());
    const counts = new Map<string, number>();
    names.forEach(name => counts.set(name, (counts.get(name) ?? 0) + 1));

    const rules: RuleTestRuleOption[] = [];
    const seen = new Set<string>();
    frozen.specifications.forEach((spec, index) => {
      const key = names[index];
      // Ohne Namen ist eine Spezifikation nicht adressierbar; doppelte Namen nur einmal anbieten.
      if (!key || seen.has(key)) return;
      seen.add(key);
      rules.push({
        key,
        shortCode: spec.identifier?.trim() ?? '',
        name: key,
        allowedFileTypes: ['IFC'],
        allowedDocumentClasses: [],
      });
    });

    const validationErrors: string[] = [];
    const ruleValidationErrors: Record<string, string[]> = {};
    for (const issue of getIdsValidationIssues(frozen)) {
      if (issue.specificationIndex == null) {
        validationErrors.push(issue.message);
        continue;
      }
      const name = names[issue.specificationIndex];
      if (name) (ruleValidationErrors[name] ??= []).push(issue.message);
    }
    for (const [name, count] of counts) {
      if (name && count > 1) {
        (ruleValidationErrors[name] ??= []).push(
          `Der Name „${name}“ kommt ${count}-mal vor – Spezifikationsnamen müssen eindeutig sein.`);
      }
    }
    if (rules.length === 0) {
      validationErrors.push('Das IDS enthält noch keine benannte Spezifikation.');
    }

    const open = this.selectedSpec?.name?.trim() || this.lastSelectedSpecName?.trim() || null;
    const ruleId = this.ruleId ?? '';
    const editorDirty = this.isDirty;
    return {
      projectId,
      projectName: this.stateConfigService.getProject()?.name ?? '',
      rulesetType: 'IDS',
      rulesetId: ruleId,
      setLabel: frozen.info.title || 'IDS',
      rules,
      preselectedRuleKey: rules.some(rule => rule.key === open) ? open : (rules[0]?.key ?? null),
      ruleLocked: false,
      setAllowedFileTypes: ['IFC'],
      setAllowedDocumentClasses: [],
      editorDirty,
      modelLabel: null,
      validationErrors,
      ruleValidationErrors,
      contextKey: this.ruleTestKey,
      buildRequest: (ruleKey, files) => ({
        rulesetType: 'IDS',
        rulesetId: ruleId,
        ruleKey,
        editorDirty,
        files,
        ids: {xml: this.idsAdapter.filterSpecificationsByName(this.idsAdapter.toXml(frozen), [ruleKey])},
      }),
      reportConfig: buildDefaultReportConfigForRuleset(ruleId, 'IDS', null),
      idsThresholds: this.currentThresholds(),
    };
  }

  /** Die aktuell eingegebenen Schwellwerte, oder `null` bei ausgeschalteter/ungültiger Eingabe. */
  private currentThresholds(): IdsThresholds | null {
    if (!this.thresholdsEnabled) return null;
    return normalizeIdsThresholds({
      warningFrom: this.thresholdWarningFrom,
      successFrom: this.thresholdSuccessFrom,
    });
  }

  /** Übernimmt die geladene Set-Konfiguration in die Formularfelder. */
  private applyLoadedConfig(configJson: string | null | undefined): void {
    this.loadedConfigJson = configJson ?? null;
    const thresholds = parseIdsThresholds(configJson);
    this.thresholdsEnabled = thresholds !== null;
    this.thresholdWarningFrom = thresholds ? String(thresholds.warningFrom) : '';
    this.thresholdSuccessFrom = thresholds ? String(thresholds.successFrom) : '';
    this.thresholdError = null;
  }

  /** Navigates the editor to the first field that is missing a required value. */
  private navigateToFirstError() {
    // 1. Metadata: title
    if (!this.rule.info.title?.trim()) {
      if (this.rule.specifications.length > 0) {
        // Open split layout with metadata on the right
        if (this.selectedSpecIndex < 0) this.selectedSpecIndex = 0;
        this.currentView = 'metadata';
      } else {
        this.currentView = 'list';
        // In list view there is no metadata editor – switch to split view
        this.selectedSpecIndex = -1;
        this.currentView = 'metadata';
      }
      return;
    }

    // 1b. Metadata: author that is not an e-mail address / invalid ISO date
    if (this.isAuthorInvalid || this.isDateInvalid) {
      if (this.rule.specifications.length > 0 && this.selectedSpecIndex < 0) {
        this.selectedSpecIndex = 0;
      }
      this.currentView = 'metadata';
      return;
    }

    // 2. Specifications
    for (let i = 0; i < this.rule.specifications.length; i++) {
      const spec = this.rule.specifications[i];

      // Spec name
      if (!spec.name?.trim()) {
        this.selectedSpecIndex = i;
        this.currentView = 'spec-detail';
        this.activeDetailTab = 'general';
        return;
      }

      // Applicability facets
      for (const facet of spec.applicability) {
        if (isFacetInvalid(facet)) {
          this.selectedSpecIndex = i;
          this.currentView = 'spec-detail';
          this.activeDetailTab = 'applicability';
          facet.expanded = true;
          return;
        }
      }

      // Requirement facets
      for (const facet of spec.requirements) {
        if (isFacetInvalid(facet)) {
          this.selectedSpecIndex = i;
          this.currentView = 'spec-detail';
          this.activeDetailTab = 'requirements';
          facet.expanded = true;
          return;
        }
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   CHATBIC-VORSCHLAG (Edit-Modus)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Wird vom `UnsavedChangesGuard` (über `IdsRuleManagementEditorComponent`) aufgerufen, wenn der
   * Nutzer beim Wegnavigieren „Verwerfen" wählt. Ein offener Vorschlag wird dabei wirklich verworfen,
   * statt stillschweigend als „gespeichert" zu gelten.
   */
  discardChanges(): DiscardOutcome {
    if (this.pendingProposal) {
      if (this.proposalHasAcceptedChanges) {
        // Scheitert das Speichern der übernommenen Änderungen an der Validierung, bleibt der Nutzer hier.
        return this.keepAcceptedChangesAndLeave() ? 'savedAccepted' : 'blocked';
      }
      this.onProposalDiscardClicked();
      return 'discarded';
    }
    this.markSaved();
    return 'discarded';
  }

  /**
   * Schreibt einen IDS-Vorschlag in den Formularzustand, OHNE zu speichern. `markSaved()` wird
   * bewusst nicht aufgerufen: `isDirty` bleibt true, gespeichert wird über den normalen Pfad.
   *
   * Übernommen wird wie beim Import mit „Ersetzen" (derselbe Merge), aber unabhängig von den
   * Import-Checkboxen. `ruleId` bleibt, Speichern überschreibt also diesen Check.
   */
  applyProposal(proposal: MyCheckProposal): void {
    const doc = proposal.document as IdsProposalDocument | undefined;
    let proposed: IDSRule;
    try {
      proposed = this.idsAdapter.fromXml(doc?.idsXml ?? '');
    } catch (err) {
      console.error('Vorschlag enthält kein lesbares IDS-XML:', err);
      this.toastService.error('Der Vorschlag von chatBIC konnte nicht gelesen werden.');
      return;
    }
    // Dieselbe Rückfallregel wie beim Laden (loadRuleFromBackend): Name und Beschreibung des Checks
    // nur, wenn das XML selbst keine enthält.
    if (!proposed.info.title && doc?.name) proposed.info.title = doc.name;
    if (!proposed.info.description && doc?.description) proposed.info.description = doc.description;

    // Ein noch offener Vorschlag (vom Backend inzwischen SUPERSEDED) steckt bereits im Formular. Erst
    // zurücksetzen, sonst wäre er der „Vorher"-Stand des neuen und ein Verwerfen fiele nicht auf den
    // gespeicherten Stand zurück. Einzeln übernommene Änderungen bleiben dabei erhalten.
    if (this.pendingProposal) {
      this.revertPendingProposal();
    }
    this.preProposalSnapshot = this.cloneRule(this.rule);
    // Beide Seiten durch denselben XML-Rundlauf, sonst melden Formunterschiede des Editor-Modells
    // unveränderte Felder als geändert.
    this.proposalDiff = computeIdsDiff(this.normalizedRule(this.rule), proposed);
    this.rule = this.mergeImportedRule(proposed, true, true);
    this.diffKeys = new WeakMap();
    this.rule.specifications.forEach(spec => {
      const key = this.specKey(spec);
      const specChanges = this.proposalDiff?.specChanges;
      if (specChanges?.changed.has(key) || specChanges?.removedFacets.has(key)) this.diffKeys.set(spec, key);
    });
    this.currentView = 'list';
    this.selectedSpecIndex = -1;
    this.pendingProposal = proposal;
    this.proposalAppliedState = this.serializeEditorState();
  }

  /**
   * Banner „Änderungen übernehmen": markiert den Vorschlag serverseitig als APPLIED und speichert
   * über denselben Pfad wie der Speichern-Button. Die Markierung darf fehlschlagen, ohne dass das
   * Speichern ausbleibt.
   */
  onProposalApplyClicked(): void {
    if (!this.pendingProposal || !this.stateConfigService.getProjectIdOrUndefined()
      || this.applyingProposalAction || !this.ruleId) return;
    // Bei einem unvollständigen Vorschlag bricht save() vor dem Übernehmen ab und zeigt die fehlenden
    // Pflichtfelder: der Vorschlag bleibt offen, statt als APPLIED zu gelten, obwohl nichts gespeichert wurde.
    this.save();
  }

  /** Übernehmen-Button eines Metadatenfelds: der Wert steht schon im Formular, nur die Markierung geht. */
  onInfoFieldAccepted(field: keyof IDSInfo): void {
    this.proposalHasAcceptedChanges = true;
    this.proposalDiff?.fieldChanges.delete(`info.${field}`);
    this.settleProposalIfResolved();
  }

  /** True, solange der Vorschlag noch mindestens eine offene (nicht einzeln entschiedene) Änderung
   *  enthält. Steuert den Banner „Änderungen übernehmen". */
  get hasOpenProposalChanges(): boolean {
    const diff = this.proposalDiff;
    if (!diff) return false;
    return diff.fieldChanges.size > 0
      || diff.specChanges.added.length > 0
      || diff.specChanges.removed.length > 0
      || diff.specChanges.changed.size > 0
      || diff.specChanges.removedFacets.size > 0;
  }

  onSpecFieldAccepted(spec: IDSSpecification, path: string): void {
    this.proposalHasAcceptedChanges = true;
    this.forgetSpecPath(spec, path);
    this.settleProposalIfResolved();
  }

  /** ✓ an einer Facette (Anforderung/Anwendbarkeit): nur diese Facette übernehmen, Markierung weg. */
  onSpecFacetAccepted(spec: IDSSpecification, block: IdsFacetBlock, index: number): void {
    const changes = this.proposalDiff?.specChanges.changed.get(this.diffKey(spec));
    if (!changes) return;
    this.proposalHasAcceptedChanges = true;
    this.facetPathsOf(changes, block, index).forEach(path => this.forgetSpecPath(spec, path));
    this.settleProposalIfResolved();
  }

  /** Banner „Verwerfen" (und `discardChanges()` bei offenem Vorschlag): Formular zurück + Backend informieren. */
  onProposalDiscardClicked(): void {
    const proposal = this.pendingProposal;
    if (!proposal) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    this.revertPendingProposal();
    if (projectId && this.ruleId) {
      this.proposalService.discard(this.ruleId, proposal.id, projectId).subscribe({
        error: err => console.error('Verwerfen im Backend fehlgeschlagen (Formular bereits zurückgesetzt):', err),
      });
    }
  }

  /** ✓ an einer neuen oder geänderten Spezifikation: so übernehmen, Markierung weg. */
  acceptSpecProposal(spec: IDSSpecification): void {
    const diff = this.proposalDiff;
    if (!diff) return;
    this.proposalHasAcceptedChanges = true;
    const addedIndex = diff.specChanges.added.indexOf(spec);
    if (addedIndex >= 0) {
      diff.specChanges.added.splice(addedIndex, 1);
      this.settleProposalIfResolved();
      return;
    }
    diff.specChanges.changed.delete(this.diffKey(spec));
    diff.specChanges.removedFacets.delete(this.diffKey(spec));
    this.settleProposalIfResolved();
  }

  /** „Entfernen" auf der Karte einer entfernten Spezifikation: Entfernen bestätigen. */
  acceptSpecRemoval(spec: IDSSpecification): void {
    const removed = this.proposalDiff?.specChanges.removed;
    const index = removed?.indexOf(spec) ?? -1;
    if (!removed || index < 0) return;
    this.proposalHasAcceptedChanges = true;
    removed.splice(index, 1);
    this.leaveRemovedSpecView(spec);
    this.settleProposalIfResolved();
  }

  /** Lädt einen offenen Vorschlag nach, falls beim Öffnen bzw. Neuladen bereits einer aussteht. */
  private loadPendingProposalIfAny(): void {
    if (this.adminGlobal || this.readOnly || !this.ruleId) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) return;
    this.proposalService.getLatestPending(this.ruleId, projectId).subscribe({
      next: proposal => {
        if (proposal) this.applyProposal(proposal);
      },
      error: err => console.error('Offener Vorschlag konnte nicht geladen werden:', err),
    });
  }

  /** Reagiert auf das `mycheckProposalReceived`-Signal des Edit-Modus-Chats. */
  private loadAndApplyProposal(proposalId: string): void {
    if (this.adminGlobal || this.readOnly || !this.ruleId) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) return;
    this.proposalService.getById(this.ruleId, proposalId, projectId).subscribe({
      next: proposal => {
        if (proposal.status === 'PENDING') this.applyProposal(proposal);
      },
      error: err => console.error('Vorschlag konnte nicht geladen werden:', err),
    });
  }

  // ─── Metadaten: je Feld ────────────────────────────────────────────────────

  infoFieldChange(field: keyof IDSInfo): MyCheckFieldChange | undefined {
    return this.proposalDiff?.fieldChanges.get(`info.${field}`);
  }

  /**
   * „Wiederherstellen" auf der Karte einer entfernten Spezifikation. Kam der Klick aus der Detailansicht
   * der Karte, öffnet sich danach die wiederhergestellte Spezifikation; in der Listenansicht bleibt es
   * dort.
   */
  rejectSpecRemoval(spec: IDSSpecification): void {
    const removed = this.proposalDiff?.specChanges.removed;
    const index = removed?.indexOf(spec) ?? -1;
    if (!removed || index < 0) return;
    const restoredIndex = this.restoreRemovedSpec(spec);
    removed.splice(index, 1);
    const wasCardView = this.currentView === 'removed-spec' && this.selectedRemovedSpec === spec;
    this.leaveRemovedSpecView(spec);
    if (wasCardView && restoredIndex >= 0) {
      this.selectSpecification(restoredIndex);
    }
    this.settleProposalIfResolved();
  }

  /** Seitenleisten-Eintrag einer entfernten Spezifikation: Karte rechts öffnen. */
  selectRemovedSpec(spec: IDSSpecification): void {
    this.selectedRemovedSpec = spec;
    this.selectedSpecIndex = -1;
    this.currentView = 'removed-spec';
  }

  isRemovedSpecSelected(spec: IDSSpecification): boolean {
    return this.currentView === 'removed-spec' && this.selectedRemovedSpec === spec;
  }

  /** Verwerfen-Button eines Metadatenfelds: Wert auf den Stand vor dem Vorschlag zurücksetzen. */
  onInfoFieldRejected(field: keyof IDSInfo): void {
    this.revertInfoField(field);
    this.proposalDiff?.fieldChanges.delete(`info.${field}`);
    this.settleProposalIfResolved();
  }

  onSpecFieldRejected(spec: IDSSpecification, path: string): void {
    this.revertSpecPath(spec, path);
    this.forgetSpecPath(spec, path);
    this.settleProposalIfResolved();
  }

  private revertInfoField(field: keyof IDSInfo): void {
    const snapshot = this.preProposalSnapshot;
    if (!snapshot) return;
    this.rule.info = {...this.rule.info, [field]: snapshot.info[field]};
  }

  // ─── Spezifikationen: je Feld ──────────────────────────────────────────────

  /** Die offenen Änderungen einer Spezifikation, für `app-ids-specification`. */
  specFieldChanges(spec: IDSSpecification | null): Map<string, MyCheckFieldChange> | undefined {
    return spec ? this.proposalDiff?.specChanges.changed.get(this.diffKey(spec)) : undefined;
  }

  /**
   * Verlassen mit einzeln übernommenen Änderungen: nur die noch offenen werden verworfen, die
   * bestätigten werden gespeichert. Ohne das gingen sie beim Verlassen verloren, obwohl der Nutzer sie
   * ausdrücklich übernommen hat. Der Vorschlag gilt dann als APPLIED (teilweise übernommen).
   *
   * Als APPLIED markiert erst {@link save} nach erfolgreichem Speichern: scheitert die Validierung oder der
   * Request, bleibt der Vorschlag offen (ohne Markierungen), und der nächste Speichern-Klick schließt ihn
   * ab – sonst wäre er serverseitig erledigt, obwohl nichts gespeichert wurde.
   *
   * @returns ob gespeichert wird – `false`, wenn die Validierung das Speichern verhindert.
   */
  private keepAcceptedChangesAndLeave(): boolean {
    const proposal = this.pendingProposal;
    if (!proposal) return false;
    this.revertPendingProposal();
    this.pendingProposal = proposal;
    // Läuft über die Komponente hinaus weiter; das Speichern braucht keinen Bildschirm.
    return this.save();
  }

  /**
   * ✗ an einer Facette: nur diese Facette auf den Stand vor dem Vorschlag zurücksetzen. Eine neue
   * Facette wird entfernt; die Pfade der nachfolgenden neuen Facetten rücken dann um eins auf.
   */
  onSpecFacetRejected(spec: IDSSpecification, block: IdsFacetBlock, index: number): void {
    const key = this.diffKey(spec);
    const changes = this.proposalDiff?.specChanges.changed.get(key);
    if (!changes) return;
    const countBefore = spec[block].length;
    const paths = this.facetPathsOf(changes, block, index);
    this.revertSpecPaths(spec, paths);
    paths.forEach(path => changes.delete(path));
    if (spec[block].length < countBefore) {
      this.shiftFacetPaths(changes, block, index);
    }
    if (changes.size === 0) this.proposalDiff!.specChanges.changed.delete(key);
    this.settleProposalIfResolved();
  }

  /** Punkt an einem Detail-Tab: hat der Vorschlag in diesem Tab der gewählten Spezifikation noch etwas offen? */
  specTabHasProposalChange(tab: SpecDetailTab): boolean {
    if (this.specRemovedFacets(this.selectedSpec)?.some(removed => removed.block === tab)) return true;
    const changes = this.specFieldChanges(this.selectedSpec);
    if (!changes) return false;
    return [...changes.keys()].some(path => this.specTabOfPath(path) === tab);
  }

  /** ✗ an einer neuen oder geänderten Spezifikation: neue entfernen, geänderte auf den Vorher-Stand setzen. */
  rejectSpecProposal(spec: IDSSpecification): void {
    const diff = this.proposalDiff;
    if (!diff) return;
    const addedIndex = diff.specChanges.added.indexOf(spec);
    if (addedIndex >= 0) {
      diff.specChanges.added.splice(addedIndex, 1);
      const index = this.rule.specifications.indexOf(spec);
      // applySpecificationRemoval schließt den Vorschlag selbst ab, falls nichts mehr offen ist. Ein zweites
      // settle liefe, während der PUT des ersten noch unterwegs ist, und schickte ein DISCARD vor das APPLY.
      if (index >= 0) {
        this.applySpecificationRemoval(index);
      } else {
        this.settleProposalIfResolved();
      }
      return;
    }
    const key = this.diffKey(spec);
    this.revertSpecPaths(spec, [...(diff.specChanges.changed.get(key)?.keys() ?? [])]);
    diff.specChanges.changed.delete(key);
    this.restoreRemovedFacets(spec, diff.specChanges.removedFacets.get(key) ?? []);
    diff.specChanges.removedFacets.delete(key);
    this.settleProposalIfResolved();
  }

  /**
   * Sind alle Änderungen des Vorschlags einzeln entschieden, wird er abgeschlossen – ohne den Banner gibt
   * es keinen „Übernehmen"-Klick mehr, der das übernähme. Mit übernommenen Änderungen werden diese
   * gespeichert und der Vorschlag als APPLIED markiert, sonst gilt er als verworfen. Ohne das bliebe er im
   * Backend offen und käme nach einem Reload erneut.
   */
  private settleProposalIfResolved(): void {
    if (!this.pendingProposal || this.hasOpenProposalChanges) return;
    if (this.proposalHasAcceptedChanges) {
      this.keepAcceptedChangesAndLeave();
    } else {
      this.onProposalDiscardClicked();
    }
  }

  isProposalSpecChanged(spec: IDSSpecification): boolean {
    const specChanges = this.proposalDiff?.specChanges;
    const key = this.diffKey(spec);
    return !!specChanges && (specChanges.changed.has(key) || specChanges.removedFacets.has(key));
  }

  /**
   * Schlüssel einer Spezifikation in `proposalDiff.specChanges.changed`: der Name, den sie beim Anwenden
   * des Vorschlags hatte. Wer den Titel danach ändert (oder verwirft), behält so seine Markierungen.
   */
  private diffKey(spec: IDSSpecification): string {
    return this.diffKeys.get(spec) ?? this.specKey(spec);
  }

  private forgetSpecPath(spec: IDSSpecification, path: string): void {
    const key = this.diffKey(spec);
    const changes = this.proposalDiff?.specChanges.changed.get(key);
    if (!changes) return;
    changes.delete(path);
    if (changes.size === 0) this.proposalDiff!.specChanges.changed.delete(key);
  }

  /** Setzt mehrere Pfade zurück; höhere Facettenindizes zuerst, damit das Entfernen neuer Facetten die
   *  Position der übrigen nicht verschiebt. */
  private revertSpecPaths(spec: IDSSpecification, paths: string[]): void {
    const facetIndex = (path: string): number => Number(path.split('.')[1] ?? -1);
    [...paths].sort((a, b) => facetIndex(b) - facetIndex(a)).forEach(path => this.revertSpecPath(spec, path));
  }

  /** Alle Pfade der Änderungen genau einer Facette (ganze Facette und einzelne Felder). */
  private facetPathsOf(changes: Map<string, unknown>, block: IdsFacetBlock, index: number): string[] {
    const whole = facetPath(block, index);
    return [...changes.keys()].filter(path => path === whole || path.startsWith(whole + '.'));
  }

  /**
   * Nach dem Entfernen der Facette `removedIndex` rücken die Pfade aller späteren Facetten um eins nach vorn;
   * mit `delta = 1` (Facette an `removedIndex` eingefügt) rücken die Pfade ab dort um eins nach hinten.
   */
  private shiftFacetPaths(changes: Map<string, MyCheckFieldChange>, block: IdsFacetBlock, removedIndex: number, delta: -1 | 1 = -1): void {
    const shifted = [...changes].map(([path, change]): [string, MyCheckFieldChange] => {
      const [pathBlock, pathIndex, ...rest] = path.split('.');
      const affected = delta < 0 ? Number(pathIndex) > removedIndex : Number(pathIndex) >= removedIndex;
      if (pathBlock !== block || pathIndex === undefined || !affected) return [path, change];
      return [[pathBlock, Number(pathIndex) + delta, ...rest].join('.'), change];
    });
    changes.clear();
    shifted.forEach(([path, change]) => changes.set(path, change));
  }

  // ─── Facetten, die der Vorschlag entfernt ──────────────────────────────────

  /** Die vom Vorschlag entfernten Facetten einer Spezifikation, für `app-ids-specification`. */
  specRemovedFacets(spec: IDSSpecification | null): RemovedIdsFacet[] | undefined {
    return spec ? this.proposalDiff?.specChanges.removedFacets.get(this.diffKey(spec)) : undefined;
  }

  /** ✓ an einer entfernten Facette: Entfernen bestätigen – sie steht schon nicht mehr im Formular. */
  acceptFacetRemoval(spec: IDSSpecification, removed: RemovedIdsFacet): void {
    if (!this.forgetRemovedFacet(spec, removed)) return;
    this.proposalHasAcceptedChanges = true;
    this.settleProposalIfResolved();
  }

  /** ✗ an einer entfernten Facette: an ihrer alten Position (gekappt) wiederherstellen. */
  rejectFacetRemoval(spec: IDSSpecification, removed: RemovedIdsFacet): void {
    if (!this.forgetRemovedFacet(spec, removed)) return;
    this.restoreRemovedFacets(spec, [removed]);
    this.settleProposalIfResolved();
  }

  /** Nimmt eine entfernte Facette aus dem Diff; `false`, wenn sie dort nicht (mehr) steht. */
  private forgetRemovedFacet(spec: IDSSpecification, removed: RemovedIdsFacet): boolean {
    const key = this.diffKey(spec);
    const removedFacets = this.proposalDiff?.specChanges.removedFacets;
    const list = removedFacets?.get(key);
    const index = list?.indexOf(removed) ?? -1;
    if (!removedFacets || !list || index < 0) return false;
    list.splice(index, 1);
    if (list.length === 0) removedFacets.delete(key);
    return true;
  }

  /**
   * Fügt entfernte Facetten an ihrer Position im Ist-Stand wieder ein (gekappt auf die Listenlänge), in
   * aufsteigender Reihenfolge. Die Pfade der dahinter liegenden offenen Facettenänderungen rücken mit.
   */
  private restoreRemovedFacets(spec: IDSSpecification, removedFacets: RemovedIdsFacet[]): void {
    const changes = this.specFieldChanges(spec);
    for (const removed of [...removedFacets].sort((a, b) => a.index - b.index)) {
      const facets = spec[removed.block];
      const index = Math.min(removed.index, facets.length);
      facets.splice(index, 0, this.clone(removed.facet));
      if (changes) this.shiftFacetPaths(changes, removed.block, index, 1);
    }
  }

  // ─── Spezifikationen: ganze Spezifikation ─────────────────────────────────

  isProposalSpecAdded(spec: IDSSpecification): boolean {
    return !!this.proposalDiff?.specChanges.added.includes(spec);
  }

  // ─── Navigation und Review-Leiste (#2789) ──────────────────────────────────

  /** Punkt an einer Spezifikation in der Navigation; „neu" geht vor „geändert". */
  specNavStatus(spec: IDSSpecification): ProposalChangeStatus | null {
    if (this.isProposalSpecAdded(spec)) return 'added';
    return this.isProposalSpecChanged(spec) ? 'changed' : null;
  }

  /** Punkt an der Metadaten-Karte. */
  get metadataNavStatus(): ProposalChangeStatus | null {
    return this.proposalDiff?.fieldChanges.size ? 'changed' : null;
  }

  /** Präfix der `data-proposal-review`-Schlüssel einer Spezifikation (`spec:<diffKey>`), für `app-ids-specification`. */
  specReviewKeyPrefix(spec: IDSSpecification): string {
    return `spec:${this.diffKey(spec)}`;
  }

  get reviewRulesetId(): string {
    return this.ruleId ?? '';
  }

  /**
   * Offene Einzelentscheidungen für die Review-Leiste im Edit-Chat, in der Reihenfolge der Navigation:
   * Metadatenfelder, dann je Spezifikation (neu = ein Eintrag, geändert = je Pfad und je entfernter
   * Facette), zuletzt entfernte Spezifikationen. `null` genau dann, wenn {@link hasOpenProposalChanges}
   * false ist.
   */
  proposalReviewSnapshot(): ProposalReviewSnapshot | null {
    const proposal = this.pendingProposal;
    const diff = this.proposalDiff;
    if (!proposal || !diff || !this.hasOpenProposalChanges) return null;
    const {added, changed, removedFacets, removed} = diff.specChanges;
    const items: ProposalReviewItem[] = [];

    for (const path of diff.fieldChanges.keys()) {
      items.push({
        key: path,
        reveal: () => {
          this.currentView = 'metadata';
          revealProposalTarget(path);
        },
      });
    }

    const coveredKeys = new Set<string>();
    const coveredAdded = new Set<IDSSpecification>();
    this.rule.specifications.forEach((spec, index) => {
      if (added.includes(spec)) {
        coveredAdded.add(spec);
        items.push({key: `spec-added:${spec.id}`, reveal: () => this.revealSpec(index, 'general')});
        return;
      }
      const diffKey = this.diffKey(spec);
      if (coveredKeys.has(diffKey)) return;
      coveredKeys.add(diffKey);
      const prefix = this.specReviewKeyPrefix(spec);
      for (const path of changed.get(diffKey)?.keys() ?? []) {
        const key = `${prefix}:${path}`;
        items.push({
          key,
          reveal: () => {
            this.revealSpec(index, this.specTabOfPath(path));
            revealProposalTarget(key);
          },
        });
      }
      for (const removedFacet of removedFacets.get(diffKey) ?? []) {
        items.push({
          key: `${prefix}:removed:${removedFacet.block}:${removedFacet.index}`,
          reveal: () => this.revealSpec(index, removedFacet.block),
        });
      }
    });

    // Offene Änderungen an Spezifikationen, die nicht mehr im Formular stehen (z. B. eine neue
    // Spezifikation, die der Nutzer über das Menü gelöscht hat): Sie halten den Vorschlag weiter offen
    // und zählen deshalb mit, sonst wiche der Zähler von `hasOpenProposalChanges` ab. Ohne eigene
    // Detailansicht führt der Stepper dort zur Liste.
    const toList = () => {
      this.currentView = 'list';
    };
    added.filter(spec => !coveredAdded.has(spec))
      .forEach(spec => items.push({key: `spec-added:${spec.id}`, reveal: toList}));
    for (const [diffKey, changes] of changed) {
      if (coveredKeys.has(diffKey)) continue;
      for (const path of changes.keys()) items.push({key: `spec:${diffKey}:${path}`, reveal: toList});
    }
    for (const [diffKey, facets] of removedFacets) {
      if (coveredKeys.has(diffKey)) continue;
      for (const removedFacet of facets) {
        items.push({key: `spec:${diffKey}:removed:${removedFacet.block}:${removedFacet.index}`, reveal: toList});
      }
    }

    for (const spec of removed) {
      items.push({key: `spec-removed:${this.specKey(spec)}`, reveal: () => this.selectRemovedSpec(spec)});
    }

    return {proposalId: proposal.id, items, stale: proposal.stale, busy: this.applyingProposalAction};
  }

  proposalReviewContext(): ProposalReviewContext | null {
    // Der gespeicherte Name wie im KI- und Hybrid-Editor, nicht ein vom Vorschlag geänderter Titel.
    return {
      mycheckName: this.preProposalSnapshot?.info.title ?? this.rule.info.title,
      itemLabel: this.currentView === 'spec-detail' ? this.selectedSpec?.name || undefined : undefined,
    };
  }

  acceptAllProposalChanges(): void {
    this.onProposalApplyClicked();
  }

  discardAllProposalChanges(): void {
    this.onProposalDiscardClicked();
  }

  /**
   * Öffnet eine Spezifikation im angegebenen Tab (Stepper der Review-Leiste). Bewusst nicht über
   * {@link selectSpecification}: das schaltet bei erneutem Aufruf für dieselbe Spezifikation zurück zur Liste.
   */
  private revealSpec(index: number, tab: SpecDetailTab): void {
    this.selectedSpecIndex = index;
    this.currentView = 'spec-detail';
    this.activeDetailTab = tab;
    this.lastSelectedSpecName = this.rule.specifications[index]?.name ?? null;
  }

  /** Detail-Tab, in dem ein Pfad aus `IdsSpecChanges.changed` angezeigt wird. */
  private specTabOfPath(path: string): SpecDetailTab {
    const block = path.split('.')[0];
    return block === 'applicability' || block === 'requirements' ? block : 'general';
  }

  /**
   * Nimmt alles zurück, was vom Vorschlag noch offen ist. Wurde seit dem Anwenden nichts angefasst, ist
   * das exakt der Stand vor dem Vorschlag. Sonst geht es vom AKTUELLEN Stand aus: einzeln übernommene
   * Felder und Spezifikationen bleiben stehen, und ebenso eigene Änderungen, die der Nutzer während des
   * offenen Vorschlags gemacht hat (früher schrieb ein „Verwerfen" ohne Einzelentscheidung den ganzen
   * Snapshot zurück und verlor sie stillschweigend). Kein `markSaved()`: eigene ungespeicherte
   * Änderungen bleiben als solche erkennbar.
   */
  private revertPendingProposal(): void {
    const diff = this.proposalDiff;
    // Ein ✓ ändert das Formular nicht, deshalb zählt es eigens: sonst fiele es hier mit zurück.
    const untouched = !this.proposalHasAcceptedChanges && this.serializeEditorState() === this.proposalAppliedState;
    if (this.preProposalSnapshot && diff && untouched) {
      // Seit dem Anwenden nichts angefasst: exakt der Stand vor dem Vorschlag. Pfadweise ginge das nicht
      // genau – die Spezifikationen stammen aus dem XML-Rundlauf des Vorschlags (neue Facetten-IDs).
      this.rule = this.preProposalSnapshot;
      this.currentView = 'list';
      this.selectedSpecIndex = -1;
    } else if (this.preProposalSnapshot && diff) {
      for (const path of [...diff.fieldChanges.keys()]) {
        this.revertInfoField(path.slice('info.'.length) as keyof IDSInfo);
      }
      for (const [key, changes] of [...diff.specChanges.changed]) {
        const spec = this.rule.specifications.find(s => this.diffKey(s) === key);
        if (spec) this.revertSpecPaths(spec, [...changes.keys()]);
      }
      for (const [key, removedFacets] of [...diff.specChanges.removedFacets]) {
        const spec = this.rule.specifications.find(s => this.diffKey(s) === key);
        if (spec) this.restoreRemovedFacets(spec, removedFacets);
      }
      for (const spec of [...diff.specChanges.added]) {
        const index = this.rule.specifications.indexOf(spec);
        if (index >= 0) this.rule.specifications.splice(index, 1);
      }
      for (const spec of [...diff.specChanges.removed]) {
        this.restoreRemovedSpec(spec);
      }
      this.currentView = 'list';
      this.selectedSpecIndex = -1;
    }
    this.clearProposalState();
  }

  /** Spezifikationen, die der Vorschlag entfernt – stehen nicht mehr in `rule.specifications`. */
  get removedProposalSpecs(): IDSSpecification[] {
    return this.proposalDiff?.specChanges.removed ?? [];
  }

  /**
   * Schließt einen Vorschlag als übernommen ab (Backend-Buchführung plus lokaler Zustand). Wird von
   * {@link save} nach erfolgreichem Speichern aufgerufen – egal ob über den Banner oder den Speichern-Button.
   * Den lokalen Zustand räumt es nur ab, wenn inzwischen kein neuerer Vorschlag angewendet wurde.
   */
  private markPendingProposalApplied(proposal: MyCheckProposal | null): void {
    if (!proposal) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (this.pendingProposal === proposal) this.clearProposalState();
    if (!projectId || !this.ruleId) return;
    this.applyingProposalAction = true;
    this.proposalService.apply(this.ruleId, proposal.id, projectId).subscribe({
      error: err => {
        console.error('Vorschlag konnte nicht als APPLIED markiert werden (wird trotzdem gespeichert):', err);
        this.applyingProposalAction = false;
      },
      complete: () => this.applyingProposalAction = false,
    });
  }

  /**
   * Setzt einen Pfad einer Spezifikation auf den Stand vor dem Vorschlag zurück (Pfadschema siehe
   * `IdsSpecChanges.changed`). Felder der Spezifikation kommen aus dem Snapshot. Facetten sind über ihre
   * Identität zugeordnet, ihr Index zählt im Vorschlag und passt nicht zur Position im Snapshot – sie
   * werden deshalb auf das `before` der Änderung zurückgesetzt (Ist-Stand im XML-Rundlauf).
   */
  private revertSpecPath(spec: IDSSpecification, path: string): void {
    const [field, index, facetField] = path.split('.');
    const target = spec as unknown as Record<string, unknown>;
    if (index === undefined) {
      const snapshotSpec = this.snapshotSpec(this.diffKey(spec));
      if (!snapshotSpec) return;
      target[field] = this.clone((snapshotSpec as unknown as Record<string, unknown>)[field]);
      return;
    }
    const change = this.specFieldChanges(spec)?.get(path);
    const facets = target[field] as IDSFacet[] | undefined;
    if (!change || !facets) return;
    if (facetField === undefined) {
      // Ganze Facette: mit Vorher-Stand zurücksetzen, sonst war sie neu → entfernen.
      if (change.before) {
        facets[Number(index)] = this.clone(change.before as IDSFacet);
      } else {
        facets.splice(Number(index), 1);
      }
      return;
    }
    const facet = facets[Number(index)] as unknown as Record<string, unknown> | undefined;
    if (facet) {
      facet[facetField] = this.clone(change.before);
    }
  }

  /** Nach der Entscheidung gibt es die Karte nicht mehr – zurück zur Listenansicht. */
  private leaveRemovedSpecView(spec: IDSSpecification): void {
    if (this.selectedRemovedSpec !== spec) return;
    this.selectedRemovedSpec = null;
    if (this.currentView === 'removed-spec') {
      this.currentView = 'list';
    }
  }

  private clearProposalState(): void {
    this.selectedRemovedSpec = null;
    if (this.currentView === 'removed-spec') this.currentView = 'list';
    this.pendingProposal = null;
    this.proposalDiff = null;
    this.preProposalSnapshot = null;
    this.proposalAppliedState = null;
    this.proposalHasAcceptedChanges = false;
  }

  /**
   * Fügt eine vom Vorschlag entfernte Spezifikation aus dem Snapshot wieder an ihrer alten Position ein.
   * Liefert die Position im Formular, oder -1, wenn der Snapshot sie nicht kennt.
   */
  private restoreRemovedSpec(spec: IDSSpecification): number {
    const snapshot = this.preProposalSnapshot;
    if (!snapshot) return -1;
    const key = this.diffKey(spec);
    const snapshotIndex = snapshot.specifications.findIndex(s => this.specKey(s) === key);
    if (snapshotIndex < 0) return -1;
    const index = Math.min(snapshotIndex, this.rule.specifications.length);
    this.rule.specifications.splice(index, 0, this.clone(snapshot.specifications[snapshotIndex]));
    return index;
  }

  private snapshotSpec(key: string): IDSSpecification | undefined {
    const snapshotKey = this.proposalDiff?.specChanges.renamedFrom.get(key) ?? key;
    return this.preProposalSnapshot?.specifications.find(s => this.specKey(s) === snapshotKey);
  }

  /** Identität einer Spezifikation für den Vorschlag – IDS kennt keine persistente Spezifikations-ID. */
  private specKey(spec: IDSSpecification): string {
    return (spec.name ?? '').trim();
  }

  /** Ist-Stand in derselben Form, die `fromXml` für den Vorschlag liefert. */
  private normalizedRule(rule: IDSRule): IDSRule {
    try {
      return this.idsAdapter.fromXml(this.idsAdapter.toXml(rule));
    } catch {
      return this.cloneRule(rule);
    }
  }

  private cloneRule(rule: IDSRule): IDSRule {
    return this.clone(rule);
  }

  private clone<T>(value: T): T {
    return value === undefined ? value : JSON.parse(JSON.stringify(value));
  }

  cancel() {
    this.navigateBack();
  }

  private navigateBack() {
    if (this.backRoute) {
      this.router.navigate(this.backRoute);
    } else {
      // Fallback seit der myCheck-Umstellung: der frühere Einstieg über die All-In-Regelauswahl
      // existiert nicht mehr, der Editor hängt am myCheck-Bereich.
      this.router.navigate(
        ['projects', this.stateConfigService.getProjectId(), 'my-checks']
      );
    }
  }

  toggleImportPopup() {
    this.showImportPopup = !this.showImportPopup;
    if (!this.showImportPopup) {
      this.resetImportState();
    }
  }

  private resetImportState() {
    this.selectedImportFileName = null;
    this.selectedImportFile = null;
    this.importType = null;
    this.importLoading = false;
    this.importCompleted = false;
    this.importResponseMessage = null;
    this.importErrorDetails = null;
    this.showImportErrorDetails = false;
    this.ifcVersion = null;
    this.levelOfInformation = null;
    this.discipline = '';
    this.importReplaceSpecifications = true;
    this.importOverwriteMetadata = true;
  }

  downloadIdsTemplate() {
    this.downloadExcel("https://de-thinkbic-public.s3.eu-central-1.amazonaws.com/IDS-Check_Vorlage.xlsx");
  }

  downloadAttributeListTemplate() {
      this.downloadExcel("https://de-thinkbic-public.s3.eu-central-1.amazonaws.com/IDS-Check_Attributliste.xlsx");
  }

  private downloadExcel(url: string): void {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = url.split('/').pop() || 'download.xlsx';
        link.click();
        window.URL.revokeObjectURL(downloadUrl);
      });
  }

  onIdsFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.selectedImportFile = file;
      this.selectedImportFileName = file.name;
      this.importType = 'ids-file';
      this.importCompleted = false;
      this.importResponseMessage = null;
    }
    input.value = '';
  }

  onIdsExcelFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.selectedImportFile = file;
      this.selectedImportFileName = file.name;
      this.importType = 'ids-excel';
      this.importCompleted = false;
      this.importResponseMessage = null;
    }
    input.value = '';
  }

  onAttributeListFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.selectedImportFile = file;
      this.selectedImportFileName = file.name;
      this.importType = 'attribute-list';
      this.importCompleted = false;
      this.importResponseMessage = null;
    }
    input.value = '';
  }

  /** Re-open the matching file input when the user clicks the selected file button */
  reopenFileInput() {
    // Trigger is handled via template ViewChild references; this is a no-op fallback.
    // The actual re-selection is done by clicking the hidden input directly in the template.
  }

  /** Extrahiert die aussagekräftigste Fehlermeldung aus einer HttpErrorResponse bzw. Exception. */
  private extractErrorDetails(err: unknown): string | null {
    const isHttpError = err instanceof HttpErrorResponse;
    let body: any = isHttpError ? err.error : null; // JSON-Body des Backends

    // Manche Endpunkte liefern den Body als (JSON-)String
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        return body.trim() || null;
      }
    }

    const candidate =
      body?.message ?? body?.detail ?? body?.error ?? body ??
      // Bei lokalen Exceptions (z.B. IDS-Parsing) die Exception-Message nutzen.
      // Bei HTTP-Fehlern ohne Body ist err.message nur Angulars generischer Text -> ignorieren.
      (isHttpError ? null : (err as Error)?.message) ?? null;
    if (!candidate) return null;
    const text = typeof candidate === 'string' ? candidate : JSON.stringify(candidate, null, 2);
    // Leerzeilen der Backend-Meldung zusammenfassen, damit die Anzeige kompakt bleibt
    return text.replace(/\n\s*\n+/g, '\n').trim() || null;
  }

  /** Setzt generischen Hinweis + Detailmeldung für die Fehleranzeige im Import-Popup. */
  private setImportError(fallbackMessage: string, err: unknown) {
    this.importErrorDetails = this.extractErrorDetails(err);
    this.importResponseMessage = this.importErrorDetails
      ? 'Ein Fehler ist aufgetreten.'
      : fallbackMessage;
    this.showImportErrorDetails = false;
    this.importLoading = false;
  }

  toggleImportErrorDetails() {
    this.showImportErrorDetails = !this.showImportErrorDetails;
  }

  startImport() {
    if (!this.selectedImportFile) return;
    this.importLoading = true;
    this.importResponseMessage = null;
    this.importErrorDetails = null;
    this.showImportErrorDetails = false;

    if (this.importType === 'ids-file') {
      // Lokale IDS-Datei (.ids/.xml) direkt parsen
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const xml = e.target.result;
          this.rule = this.mergeImportedRule(this.idsAdapter.fromXml(xml));
          // ruleId bewusst NICHT zurücksetzen: Wird in einen bestehenden Check importiert,
          // soll ein anschließendes Speichern diesen Check überschreiben (nicht neu anlegen).
          this.selectedSpecIndex = this.rule.specifications.length > 0 ? 0 : -1;
          this.currentView = 'list';
          this.importCompleted = true;
          this.importResponseMessage = 'Import erfolgreich.';
          this.autoSaveAfterImport();
        } catch (err) {
          console.error('Error parsing IDS file:', err);
          this.setImportError('Die Datei konnte nicht als IDS-Datei gelesen werden. Bitte prüfe, ob eine gültige IDS-Datei hochgeladen wurde.', err);
        }
        this.importLoading = false;
      };
      reader.onerror = () => {
        console.error('Error reading IDS file:', reader.error);
        this.importResponseMessage = 'Die Datei konnte nicht gelesen werden. Bitte versuche es erneut.';
        this.importLoading = false;
      };
      reader.readAsText(this.selectedImportFile);

    } else if (this.importType === 'ids-excel') {
      this.idsExcelImport.importIdsTemplate(this.selectedImportFile).then(
        (result) => this.applyImportResult(result),
        (err) => {
          console.error('Error converting IDS Excel to IDS:', err);
          this.setImportError('Fehler beim Konvertieren der IDS-Vorlage.', err);
        }
      );

    } else if (this.importType === 'attribute-list') {
      const trade = this.discipline?.trim() || undefined;
      const ifcVer = (this.ifcVersion as AttributeListIfcVersion) || undefined;
      const lodLevel = this.levelOfInformation ?? undefined;
      this.idsExcelImport.importAttributeList(this.selectedImportFile, {trade, ifcVersion: ifcVer, lodLevel}).then(
        (result) => this.applyImportResult(result),
        (err) => {
          console.error('Error converting attribute list to IDS:', err);
          this.setImportError('Fehler beim Konvertieren der Attributliste.', err);
        }
      );
    }
  }

  /**
   * Übernimmt das Ergebnis eines clientseitigen Excel-Imports (IDS-Vorlage / Attributliste) in den Editor.
   * Warnungen landen in importErrorDetails statt direkt in der Kopfzeile, damit lange Meldungen das Popup
   * nicht in die Breite ziehen.
   */
  private applyImportResult(result: IdsImportResult) {
    const details = result.warnings.length ? result.warnings.join('\n') : null;

    if (result.rule.specifications.length === 0) {
      this.importResponseMessage = 'Es wurden keine verwertbaren Daten in der Datei gefunden.';
      this.importErrorDetails = details;
      this.showImportErrorDetails = false;
      this.importLoading = false;
      return;
    }

    this.rule = this.mergeImportedRule(result.rule);
    // ruleId bewusst NICHT zurücksetzen: Wird in einen bestehenden Check importiert,
    // soll ein anschließendes Speichern diesen Check überschreiben (nicht neu anlegen).
    this.selectedSpecIndex = 0;
    this.currentView = 'list';
    this.importCompleted = true;
    this.importResponseMessage = 'Import erfolgreich.';
    this.importErrorDetails = details;
    this.showImportErrorDetails = false;
    this.importLoading = false;
    this.autoSaveAfterImport();
  }

  /**
   * Speichert direkt nach einem erfolgreichen Import, statt auf ein manuelles "Speichern" zu warten.
   *
   * <p>Grund: Werden mehrere Dateien nacheinander importiert (Anhängen-Modus, "Bestehende Bauteile
   * ersetzen" deaktiviert), ohne dass zwischendurch gespeichert wird, gingen beim abschließenden
   * einmaligen Speichern alle bis auf die letzten ~10 importierten Spezifikationen kommentarlos
   * verloren - ein bestätigter Datenverlust-Bug. Speichert jeder Import einzeln sofort, kann dieser
   * Zustand gar nicht erst entstehen. Schlägt die Validierung fehl (z.B. weil der Titel fehlt),
   * bricht save() wie gewohnt mit einer Warnung ab - der Import selbst bleibt davon unberührt, nur
   * das automatische Speichern wird übersprungen.
   */
  private autoSaveAfterImport(): void {
    this.save();
  }

  /**
   * @param replaceSpecifications Default: die Import-Checkbox. Ein chatBIC-Vorschlag erzwingt immer
   *   `true`, unabhängig vom Stand des (evtl. gar nicht offenen) Import-Popups.
   * @param overwriteMetadata Analog für die Metadaten-Checkbox.
   */
  private mergeImportedRule(
    importedRule: IDSRule,
    replaceSpecifications = this.importReplaceSpecifications,
    overwriteMetadata = this.importOverwriteMetadata,
  ): IDSRule {
    const specifications = replaceSpecifications
      ? importedRule.specifications
      : [...this.rule.specifications, ...importedRule.specifications];

    const info = overwriteMetadata ? importedRule.info : this.rule.info;

    return {info, specifications};
  }

  private loadFromXml(xml: string) {
    this.rule = this.idsAdapter.fromXml(xml);
    // ruleId bewusst NICHT zurücksetzen: Beim Import (z.B. Excel-Vorlage) in einen bereits
    // geöffneten Check soll das Speichern den bestehenden Check überschreiben statt einen neuen anzulegen.
    this.selectedSpecIndex = this.rule.specifications.length > 0 ? 0 : -1;
    this.currentView = 'list';
  }

  exportIDS() {
    const xml = this.idsAdapter.toXml(this.rule);
    this.downloadFile(xml, (this.rule.info.title || 'ids-export') + '.ids', 'application/xml');
  }

  toggleExportPopup() {
    this.showExportPopup = !this.showExportPopup;
    if (!this.showExportPopup) {
      this.exportTab = 'ids';
      this.selectedRevitFileName = null;
      this.selectedRevitFile = null;
      this.revitExportResultMessage = null;
      this.revitExportWarnings = null;
      this.excelExportLoading = false;
      this.excelExportErrorMessage = null;
    }
  }

  onRevitFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.selectedRevitFile = file;
    this.selectedRevitFileName = file.name;
    this.revitExportResultMessage = null;
    this.revitExportWarnings = null;
  }

  reopenRevitFileInput() {
    this.selectedRevitFile = null;
    this.selectedRevitFileName = null;
    this.revitExportResultMessage = null;
    this.revitExportWarnings = null;
  }

  /**
   * Liest optional die hochgeladene Altdatei ein (für GUID-Kontinuität, #2305) und erzeugt die
   * Shared-Parameter-Datei aus den aktuellen Anforderungen.
   */
  exportRevitSharedParameters() {
    if (!this.selectedRevitFile) {
      this.runRevitExport(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        this.runRevitExport(parseSharedParameterFile(e.target.result as string));
      } catch (err) {
        console.error('Error parsing shared parameter file:', err);
        this.revitExportResultMessage = 'Die hochgeladene Datei konnte nicht als Shared-Parameter-Datei gelesen werden.';
        this.revitExportWarnings = null;
      }
    };
    reader.onerror = () => {
      console.error('Error reading shared parameter file:', reader.error);
      this.revitExportResultMessage = 'Die Datei konnte nicht gelesen werden. Bitte versuche es erneut.';
      this.revitExportWarnings = null;
    };
    reader.readAsText(this.selectedRevitFile);
  }

  private runRevitExport(existing: ExistingSharedParameterFile | null) {
    const options: RevitExportOptions = {
      groupBy: this.revitExportGroupBy,
      nameScheme: this.revitExportNameScheme,
      fallbackDataType: this.revitExportFallbackDataType,
      keepOrphanedParams: this.revitExportKeepOrphans,
      excludedProperties: parseExclusionList(this.revitExportExcludedProperties),
      excludedPropertySets: parseExclusionList(this.revitExportExcludedPropertySets),
    };
    const result = buildSharedParameterFile(this.rule, options, existing);

    this.downloadFile(result.fileText, (this.rule.info.title || 'shared-parameters') + '.txt', 'text/plain');

    const warnings: string[] = [];
    if (result.skippedInvalidFacets > 0) {
      warnings.push(`${result.skippedInvalidFacets} Eigenschaft(en) ohne eindeutigen Namen (Muster/Bereich) wurden übersprungen.`);
    }
    if (result.skippedByFilter > 0) {
      warnings.push(`${result.skippedByFilter} Eigenschaft(en) wurden durch die Ausschlusslisten nicht exportiert.`);
    }
    if (result.skippedDuplicateNames.length > 0) {
      const counts = new Map<string, number>();
      for (const name of result.skippedDuplicateNames) counts.set(name, (counts.get(name) ?? 0) + 1);
      const formatted = [...counts.entries()].map(([name, count]) => count > 1 ? `${name} (${count}x)` : name).join(', ');
      warnings.push(`Mehrfach vorkommende Namen wurden nur einmal übernommen: ${formatted}`);
    }
    this.revitExportWarnings = warnings.length ? warnings : null;
    this.revitExportResultMessage = `${result.paramCount} Parameter in ${result.groupCount} Gruppe(n) exportiert`
      + (result.orphanedKept > 0 ? ` (davon ${result.orphanedKept} unverändert aus der Altdatei übernommen)` : '') + '.';
  }

  private downloadFile(content: string, filename: string, type: string) {
    this.downloadBlob(new Blob([content], {type}), filename);
  }

  private downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Dateiname für einen Excel-Export: Dateityp + IDS-Titel, z.B. "IDS-Attributliste_Aussenwaende Brandwiderstand.xlsx". */
  private buildExcelExportFilename(typePrefix: string): string {
    const title = (this.rule.info.title || '').trim().replace(/[\\/?*[\]:]/g, '-');
    return (title ? `${typePrefix}_${title}` : typePrefix) + '.xlsx';
  }

  /** Exportiert das aktuelle Regelwerk als befüllte "IDS-Vorlage" (.xlsx, siehe `IdsExcelExportService`). */
  exportIdsVorlage() {
    this.excelExportLoading = true;
    this.excelExportErrorMessage = null;
    this.idsExcelExport.exportIdsTemplate(this.rule).then(
      (blob) => {
        this.downloadBlob(blob, this.buildExcelExportFilename('IDS-Vorlage'));
        this.excelExportLoading = false;
      },
      (err) => {
        console.error('Error exporting IDS template:', err);
        this.excelExportErrorMessage = 'Die IDS-Vorlage konnte nicht exportiert werden.';
        this.excelExportLoading = false;
      }
    );
  }

  /** Exportiert das aktuelle Regelwerk als befüllte "Attributliste" (.xlsx, siehe `IdsExcelExportService`). */
  exportAttributliste() {
    this.excelExportLoading = true;
    this.excelExportErrorMessage = null;
    this.idsExcelExport.exportAttributeList(this.rule).then(
      (blob) => {
        this.downloadBlob(blob, this.buildExcelExportFilename('IDS-Attributliste'));
        this.excelExportLoading = false;
      },
      (err) => {
        console.error('Error exporting attribute list:', err);
        this.excelExportErrorMessage = 'Die Attributliste konnte nicht exportiert werden.';
        this.excelExportLoading = false;
      }
    );
  }
}
